import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FaArrowRight, FaSync, FaLayerGroup, FaSearch,
  FaFolder, FaFolderOpen, FaEdit, FaTrash,
  FaCheck, FaTimes, FaArrowUp, FaArrowDown, FaFolderPlus, FaPlus, FaCopy, FaFile,
  FaExternalLinkAlt, FaShareAlt, FaClone, FaTag,
  FaPen, FaSave, FaUndo, FaObjectGroup
} from "react-icons/fa";
import { API_BASE } from "../api/config";
import { cleanFilenameForTrello, buildTrelloTitle } from "../utils/trelloUtils";
import useVerschiebenJobs from "../utils/useVerschiebenJobs";
import VerschiebenJobIndicator from "../components/VerschiebenJobIndicator";
import EnginePicker from "../components/EnginePicker";
import useDragPanel from "../utils/useDragPanel";
import { contextShareProps, shareFile, kachelDragProps } from "../utils/dragOut";
import PdfEditLayer, { EDIT_TOOLS, EDIT_FARBEN } from "../components/pdf/PdfEditLayer";
import "./VerschiebenPage.css";
import StammdatenAnlegenDialog from "../components/stammdaten/StammdatenAnlegenDialog";

/* == API Helper == */
const api = (path, opts = {}) =>
  fetch(API_BASE + "/zuordnen/api" + path, {
    headers: { "Content-Type": "application/json", ...opts.headers },
    ...opts,
  }).then(async (r) => {
    if (!r.ok) {
      let detail = "";
      try { const j = await r.json(); detail = j.detail || ""; } catch {}
      throw new Error("API " + r.status + (detail ? ": " + detail : ""));
    }
    return r.json();
  });

const apiPost = (path, body) =>
  fetch(API_BASE + "/zuordnen/api" + path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).then(async (r) => {
    if (!r.ok) {
      let detail = "";
      try { const j = await r.json(); detail = j.detail || ""; } catch {}
      throw new Error("API " + r.status + (detail ? ": " + detail : ""));
    }
    return r.json();
  });

/* == Confidence Styles == */
const CONF = {
  green:      { bg: "#0d3320", border: "#2e7d32", text: "#81c784", label: "Sicher",      icon: "●" },
  yellow:     { bg: "#3e2723", border: "#e65100", text: "#ffb74d", label: "Möglich",   icon: "◐" },
  red:        { bg: "#3b1010", border: "#c62828", text: "#ef9a9a", label: "Unbekannt",   icon: "○" },
  manuell:    { bg: "#1a1040", border: "#7b1fa2", text: "#ce93d8", label: "Manuell",     icon: "★" },
  wiederholen:{ bg: "#0d2233", border: "#0288d1", text: "#81d4fa", label: "Wiederholen", icon: "↺" },
  auto:       { bg: "#0d2e2e", border: "#00bcd4", text: "#4dd0e1", label: "Auto",        icon: "⚡" },
  duplikat:   { bg: "#1a0a2e", border: "#6a0dad", text: "#da70d6", label: "Duplikat",    icon: "⊘" },
  todo:       { bg: "#33101f", border: "#d81b60", text: "#f48fb1", label: "Wiedervorlage", icon: "⏳" },
  none:       { bg: "#1a1a2e", border: "#444",    text: "#888",    label: "",             icon: "" },
};

/* Gruppen-Reihenfolge für die Datei-Liste */
const GROUP_ORDER = [
  { key: "auto",        label: "Auto",        icon: "⚡" },
  { key: "green",       label: "Sicher",      icon: "●" },
  { key: "yellow",      label: "Möglich",   icon: "◐" },
  { key: "red",         label: "Unbekannt",   icon: "○" },
  { key: "duplikat",    label: "Duplikat",    icon: "⊘" },
  { key: "manuell",     label: "Manuell",     icon: "★" },
  { key: "wiederholen", label: "Wiederholen", icon: "↺" },
  { key: "none",        label: "Ohne Status", icon: "–" },
  { key: "todo",        label: "Wiedervorlage", icon: "⏳" },
];

// PDF-Editor-Layer (Welle 91) liegt seit Welle 136 in components/pdf/PdfEditLayer.jsx

/* Ziel-Ordner-Eingabe — gekapselt + memoisiert, damit Tippen NICHT die ganze
   Datei-Liste neu rendert (Freeze-Fix). Hält einen lokalen Draft, übernimmt
   externe Änderungen (z.B. Ordner-Pick) nur wenn gerade nicht getippt wird,
   und committet debounced (350 ms) bzw. beim Verlassen des Feldes. */
const ZielInput = React.memo(function ZielInput({ value, onCommit, onClick, placeholder, title }) {
  const [draft, setDraft] = React.useState(value || "");
  const editingRef = React.useRef(false);
  const timerRef = React.useRef(null);
  React.useEffect(() => {
    if (!editingRef.current) setDraft(value || "");
  }, [value]);
  const schedule = (v) => {
    setDraft(v);
    editingRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { editingRef.current = false; onCommit(v); }, 350);
  };
  const flush = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    editingRef.current = false;
    onCommit(draft);
  };
  return (
    <input
      className="v-ziel-input"
      value={draft}
      onChange={(e) => schedule(e.target.value)}
      onBlur={flush}
      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); flush(); } }}
      placeholder={placeholder}
      title={title}
      onClick={onClick}
    />
  );
});

/* Schema-Optionen für manuellen Override bei Pipeline 1 */
const SCHEMA_OPTIONS = [
  { value: "rechnung",              label: "Rechnung" },
  { value: "kontoauszug",           label: "Kontoauszug" },
  { value: "ausgangsrechnung",      label: "Ausgangsrechnung" },
  { value: "mahnung",               label: "Mahnung" },
  { value: "finanzamt",             label: "Finanzamt" },
  { value: "bwa",                   label: "BWA" },
  { value: "versicherung",          label: "Versicherung" },
  { value: "nebenkostenabrechnung", label: "Nebenkostenabrechnung" },
  { value: "sonstiges",             label: "Sonstiges" },
];

/* Helper: Dateigröße formatieren */
const fmtSize = (bytes) => {
  if (bytes == null) return null;
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

/* Helper: ISO-Datum hübsch formatieren */
const fmtDate = (d) => {
  if (!d) return null;
  try {
    const dt = new Date(d);
    return dt.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }) + " " +
           dt.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  } catch { return d; }
};

// Welle 86: Platzhalter im Dateinamen (wie backend/batch._filename_hard_error)
function hatPlatzhalter(name) {
  const n = String(name || "");
  return n.startsWith("#") || n.includes("###") || n.includes("???") || /xxx/i.test(n) || n.includes("####-##-##") || /(^|\s-\s)#/.test(n);
}

function VerschiebenPage() {
  const navigate = useNavigate();

  // Body-Scroll sperren solange diese Seite aktiv ist
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // Phase: loading -> ready -> analysing -> analysed
  const [phase, setPhase] = useState("loading");
  const [analysing, setAnalysing] = useState(false); // Analyse läuft im Hintergrund (Liste bleibt bedienbar)
  const runAnalyseRef = useRef(null); // loadDateien stößt die Analyse automatisch an
  // Beim Verlassen der Seite alle laufenden Lade-/Analyse-Requests abbrechen —
  // sonst blockieren sie die Browser-Verbindungen und die nächste Seite lädt zäh.
  const abortRef = useRef(null);
  useEffect(() => () => { abortRef.current?.abort(); }, []);
  const _neuerAbort = () => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    return abortRef.current.signal;
  };
  const _istAbbruch = (e) => e?.name === "AbortError" || /abort/i.test(e?.message || "");
  const [loadMsg, setLoadMsg] = useState("Dateien laden...");
  const [error, setError] = useState(null);
  const [dateien, setDateien] = useState([]);
  const [outbox, setOutbox] = useState("");
  const [snapshotCount, setSnapshotCount] = useState(0);
  const [analysed, setAnalysed] = useState(false);
  const [berechnetAm, setBerechnetAm] = useState(null);
  const [vonCache, setVonCache] = useState(false);
  const [scanLaeuft, setScanLaeuft] = useState(false); // Hintergrund-Scan läuft gerade
  const scanPollRef = useRef(null); // setInterval-Handle für Status-Polling

  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbLoading, setThumbLoading] = useState(false);

  /* == PDF-Viewer State == */
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pdfPage, setPdfPage] = useState(0);
  const [pdfNumPages, setPdfNumPages] = useState(0);
  const [pdfZoom, setPdfZoom] = useState(1);
  const [pdfRotation, setPdfRotation] = useState(0);
  const [pdfPan, setPdfPan] = useState({ x: 0, y: 0 });
  const pdfCanvasRef = useRef(null);
  const pdfTextLayerRef = useRef(null);
  const pdfIsPanning = useRef(false);
  const pdfPanStart = useRef({ x: 0, y: 0 });
  const pdfPanOffset = useRef({ x: 0, y: 0 });
  const pdfDocCache = useRef({});
  const pdfRenderTask = useRef(null);
  const pdfjsRef = useRef(null);

  /* == PDF-Editor State (Welle 91, nur für todo-Dokumente) == */
  const [editMode, setEditMode] = useState(false);
  const [editTool, setEditTool] = useState("text");
  const [editAnnos, setEditAnnos] = useState([]);        // PDF-Punkte, alle Seiten
  const [editFelder, setEditFelder] = useState(null);    // { name: {wert, typ, seite, rect, geaendert} }
  const [editAktiveId, setEditAktiveId] = useState(null);
  // Welle 136: Seiten zum Löschen (0-basiert, wirkt beim Speichern) + Vereinen mit einem zweiten Dokument
  const [editLoeschen, setEditLoeschen] = useState([]);
  const [vereinenOffen, setVereinenOffen] = useState(false);
  const [vereinenMit, setVereinenMit] = useState(null);      // gewählte Datei, wartet auf Bestätigung
  const [vereinenSuche, setVereinenSuche] = useState("");
  const [vereinenLaeuft, setVereinenLaeuft] = useState(false);
  const [editGroesse, setEditGroesse] = useState(12);
  const [editFarbe, setEditFarbe] = useState("#000000");
  const [editSaving, setEditSaving] = useState(false);
  const [pdfReloadTick, setPdfReloadTick] = useState(0); // erzwingt Neuladen nach Speichern
  const [pdfVp, setPdfVp] = useState(null);              // aktueller pdf.js-Viewport fürs Edit-Layer
  const [fileSettings, setFileSettings] = useState({});
  const fileSettingsRef = useRef({});  // Welle 78: synchroner Zugriff nach analyseEinzel
  useEffect(() => { fileSettingsRef.current = fileSettings; }, [fileSettings]);
  const [browseOpen, setBrowseOpen] = useState(false);
  const [browsePath, setBrowsePath] = useState("");
  const [browseItems, setBrowseItems] = useState([]);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [moving, setMoving] = useState(false);
  const [moveResult, setMoveResult] = useState(null);
  const [showBatch, setShowBatch] = useState(false);
  const [batchMoving, setBatchMoving] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  const [checkedPaths, setCheckedPaths] = useState(new Set());
  const [autoMoving, setAutoMoving] = useState(false);
  const [autoResult, setAutoResult] = useState(null);
  const [inFlightPaths, setInFlightPaths] = useState(new Set()); // Dateien in laufenden Verschiebe-Jobs
  const [jobVorlauf, setJobVorlauf] = useState(""); // z. B. "Duplikat-Prüfung läuft…" vor dem Job-Start
  const { dragOffset: browseDrag, onDragStart: onBrowseDragStart } = useDragPanel(); // Ordner-Browser verschiebbar
  const [sourceFolder, setSourceFolder] = useState(""); // custom source folder (empty = default)
  const [searchParams] = useSearchParams();
  const rootParam = searchParams.get("root");
  useEffect(() => {
    if (rootParam === "firmen") setSourceFolder("/Holthey Immobilien/Firmen");
    else if (rootParam === "objekte") setSourceFolder("/Holthey Immobilien/Objekte");
  }, [rootParam]);
  const [manuellOrdner, setManuellOrdner] = useState(""); // DROPBOX_MANUELL_VERSCHIEBEN
  const [wiederholenOrdner, setWiederholenOrdner] = useState(""); // DROPBOX_INBOX
  const [editingPath, setEditingPath] = useState(null);   // Pfad der gerade editierten Datei
  const [editingName, setEditingName] = useState("");     // aktueller Eingabewert
  const [confirmDeletePath, setConfirmDeletePath] = useState(null);
  const [srcBrowseOpen, setSrcBrowseOpen] = useState(false);

  /* == Duplikat-Check State == */
  const [duplikatInfo, setDuplikatInfo] = useState(null);     // { quell, existierend, zielOrdner, onDelete, onAbort }
  const [duplikatChecking, setDuplikatChecking] = useState(false);
  const [duplikatList, setDuplikatList] = useState([]);       // für Batch: [{quell, existierend}]
  const [srcBrowsePath, setSrcBrowsePath] = useState("");
  const [srcBrowseItems, setSrcBrowseItems] = useState([]);
  const [srcBrowseLoading, setSrcBrowseLoading] = useState(false);
  const [newFolderMode, setNewFolderMode] = useState(false);     // Ziel-Browse: neuer Ordner
  const [newFolderName, setNewFolderName] = useState("");
  const [srcNewFolderMode, setSrcNewFolderMode] = useState(false); // Src-Browse: neuer Ordner
  const [srcNewFolderName, setSrcNewFolderName] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState(new Set(["todo"]));
  const [lockedGroups, setLockedGroups]       = useState({});  // Gruppe eingefroren bei Analyse → kein Springen beim Bearbeiten
  /* Welle 85: EIN Freeze-Mechanismus. lockedRef = eingefrorene Sektion je Pfad (synchron), lockedGroups = State-Spiegel.
     Eine Sektion wird beim ersten Sichtbarwerden festgelegt und danach nie überschrieben — nur die Farbe ändert sich.
     Ausnahmen: Umbenennen zieht das Lock auf den neuen Pfad, die bewusste Statuswahl (Icons/Dropdown) wirkt beim
     Zeilenwechsel (frozenPath), „Neu laden“/„Neuer Cache“ setzen alles zurück. */
  const lockedRef = useRef({});
  const _syncLocks = useCallback(() => setLockedGroups({ ...lockedRef.current }), []);
  const lockSection = useCallback((path, conf) => {
    if (!path || path in lockedRef.current) return;
    lockedRef.current[path] = conf || "none";
    _syncLocks();
  }, [_syncLocks]);
  const lockMissing = useCallback((entries) => {
    let changed = false;
    for (const [pth, conf] of entries) {
      if (pth && !(pth in lockedRef.current)) { lockedRef.current[pth] = conf || "none"; changed = true; }
    }
    if (changed) _syncLocks();
  }, [_syncLocks]);
  const moveLock = useCallback((oldPath, newPath) => {
    if (!(oldPath in lockedRef.current)) return;
    const v = lockedRef.current[oldPath];
    delete lockedRef.current[oldPath];
    if (newPath && !(newPath in lockedRef.current)) lockedRef.current[newPath] = v;
    _syncLocks();
  }, [_syncLocks]);
  const releaseLock = useCallback((path) => {
    if (!(path in lockedRef.current)) return;
    delete lockedRef.current[path];
    _syncLocks();
  }, [_syncLocks]);
  const resetLocks = useCallback(() => { lockedRef.current = {}; setLockedGroups({}); }, []);
  const [filterText, setFilterText] = useState("");           // Freitext-Filter (Multi-Term)
  const [filterPrefix, setFilterPrefix] = useState("");       // Prefix-Filter
  const [filterDatum, setFilterDatum] = useState("");         // Datum-Filter
  const [groupMode, setGroupMode] = useState("ampel");        // "ampel" | "prefix" — Sortierung/Gruppierung
  const [duplikatPickerFile, setDuplikatPickerFile] = useState(null); // Duplikat-Auswahl-Popup
  const [ordnerSucheQuery, setOrdnerSucheQuery] = useState("");       // Ordnersuche im Browse-Panel
  const [ordnerSucheTreffer, setOrdnerSucheTreffer] = useState([]);   // Treffer der Ordnersuche
  const [analysingPath, setAnalysingPath] = useState(null);   // Einzel-Analyse läuft für diesen Pfad
  const [reprocessingPath, setReprocessingPath] = useState(null); // Reprocess (OCR-Pipeline) läuft
  const [pipelinePickerFile, setPipelinePickerFile] = useState(null); // Pipeline-Auswahl-Popup
  const [pipelineSchemaOverride, setPipelineSchemaOverride] = useState(""); // Schema-Override für Pipeline 1
  // Pipeline 2 — Claude Haiku / Gemma 4
  const [ki2SessionId, setKi2SessionId] = useState(null);       // Laufende Session-ID
  const [ki2Result, setKi2Result] = useState(null);              // Fertiges Ergebnis
  const [ki2File, setKi2File] = useState(null);                  // Datei die analysiert wird
  const [ki2Polling, setKi2Polling] = useState(false);           // Polling läuft
  const [ki2Feedback, setKi2Feedback] = useState("");            // Feedback-Text
  const [ki2FeedbackLoading, setKi2FeedbackLoading] = useState(false); // Feedback läuft
  const [ki2Model, setKi2Model] = useState("claude");            // "claude" | "gemma"
  const ki2PollRef = React.useRef(null);
  // Welle 75: Gedächtnis-Chat — Verlauf früherer Runden + „Als Regel merken"
  const [ki2Chat, setKi2Chat] = useState([]);                    // [{korrektur, result}]
  const [nokPickerFile, setNokPickerFile] = useState(null);            // NOK-Art-Auswahl-Popup

  /* == Trello Popup State == */
  const [trelloPopupOpen, setTrelloPopupOpen] = useState(false);
  const [trelloPopupPath, setTrelloPopupPath] = useState(null);   // Pfad der Datei für die Popup gilt
  const [trelloGrund, setTrelloGrund] = useState("");
  const [trelloTitel, setTrelloTitel] = useState("");
  const [trelloSaving, setTrelloSaving] = useState(false);
  const [trelloError, setTrelloError] = useState("");
  const [trelloSuccess, setTrelloSuccess] = useState("");

  /* == STRG DB-Eintrag Popup State == */
  const [strgOpen, setStrgOpen] = useState(false);
  const [strgFilePath, setStrgFilePath] = useState(null);
  const [strgTable, setStrgTable] = useState("absender");
  const [strgForm, setStrgForm] = useState({});
  const [strgSaving, setStrgSaving] = useState(false);
  const [strgError, setStrgError] = useState("");
  const [strgSuccess, setStrgSuccess] = useState("");
  // Welle 20: dynamische Felder + Edit-Modus
  const [strgMode, setStrgMode] = useState("new"); // 'new' | 'edit'
  const [strgColumns, setStrgColumns] = useState({}); // realTable -> [{field,type,null,default,extra}]
  const [strgRows, setStrgRows] = useState([]);
  const [strgRowsLoading, setStrgRowsLoading] = useState(false);
  const [strgSearch, setStrgSearch] = useState("");
  const [strgEditId, setStrgEditId] = useState(null);
  const [strgEditOriginal, setStrgEditOriginal] = useState(null);
  const [adressenList, setAdressenList] = useState(null); // lazy geladen

  const STRG_TABLES = [
    { key: "absender", label: "Absender", table: "absender" },
    { key: "person", label: "Empf. Person", table: "empfaenger_person" },
    { key: "objekt", label: "Empf. Objekt", table: "empfaenger_objekt" },
    // Welle 131: Vertrag, IBAN, Versicherung, Steuernummer (+ Darlehen) → neue Maske „Stammdaten (DB 2)"
    { key: "mandat", label: "Empf. Mandat", table: "empfaenger_mandat" },
    { key: "schema_keyword", label: "Schema-Keyword", table: "rename_schema_keywords" },
  ];
  const strgRealTable = (STRG_TABLES.find(t => t.key === strgTable) || {}).table || strgTable;

  // Spalten ausblenden: Auto-Increment-IDs, Timestamps, abgeleitete Spalten.
  const STRG_HIDDEN_COLS = new Set([
    "erstellt_am", "created_at", "updated_at", "geaendert_am",
    "versicherungsnummer_normalized", "excel_row",
  ]);

  // Welle 21.D: Spaltenname → lesbares Label ("objekt_id" → "Objekt ID").
  // Generisch (kein Hardcode pro Spalte), damit neue DB-Spalten automatisch passen.
  const STRG_LABEL_UPPER = new Set(["id", "iban", "bic", "plz", "nr", "url", "az", "kred"]);
  const strgPrettyLabel = (field) =>
    String(field).split("_").filter(Boolean).map(w =>
      STRG_LABEL_UPPER.has(w.toLowerCase())
        ? w.toUpperCase()
        : w.charAt(0).toUpperCase() + w.slice(1)
    ).join(" ");

  // DB-Typ → verständlicher Placeholder ("varchar(20)" → "Text (max. 20 Zeichen)")
  const strgPlaceholder = (type) => {
    const t = String(type || "").toLowerCase();
    let m = t.match(/^(?:var)?char\((\d+)\)/);
    if (m) return `Text (max. ${m[1]} Zeichen)`;
    if (/^(tiny|medium|long)?text/.test(t)) return "Text";
    if (/^tinyint\(1\)/.test(t)) return "0 = nein, 1 = ja";
    if (/^(int|tinyint|smallint|bigint)/.test(t)) return "Ganze Zahl";
    m = t.match(/^decimal\(\d+,(\d+)\)/);
    if (m) return `Zahl (max. ${m[1]} Nachkommastellen)`;
    if (/^(decimal|float|double)/.test(t)) return "Zahl";
    return "";
  };
  const strgVisibleColumns = useCallback((realTable) => {
    const cols = strgColumns[realTable] || [];
    return cols.filter(c =>
      c.extra !== "auto_increment" &&
      !STRG_HIDDEN_COLS.has(c.field) &&
      !String(c.default || "").toLowerCase().startsWith("current_timestamp")
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strgColumns]);

  const strgLoadColumns = useCallback(async (realTable) => {
    if (strgColumns[realTable]) return;
    try {
      const resp = await fetch(API_BASE + "/database/table/" + realTable + "/columns");
      if (!resp.ok) throw new Error("API " + resp.status);
      const j = await resp.json();
      setStrgColumns(prev => ({ ...prev, [realTable]: j.columns || [] }));
    } catch (e) {
      setStrgError("Spalten laden fehlgeschlagen: " + e.message);
    }
  }, [strgColumns]);

  const strgLoadRows = useCallback(async (realTable) => {
    setStrgRowsLoading(true);
    try {
      const resp = await fetch(API_BASE + "/database/table/" + realTable + "?limit=2000");
      if (!resp.ok) throw new Error("API " + resp.status);
      const j = await resp.json();
      setStrgRows(j.rows || []);
    } catch (e) {
      setStrgError("Einträge laden fehlgeschlagen: " + e.message);
      setStrgRows([]);
    } finally {
      setStrgRowsLoading(false);
    }
  }, []);

  const strgLoadAdressen = useCallback(async () => {
    if (adressenList !== null) return adressenList;
    try {
      const resp = await fetch(API_BASE + "/database/table/adressen?limit=5000");
      if (!resp.ok) throw new Error("API " + resp.status);
      const j = await resp.json();
      const rows = j.rows || [];
      setAdressenList(rows);
      return rows;
    } catch {
      setAdressenList([]);
      return [];
    }
  }, [adressenList]);

  // Defaults aus DESCRIBE als Formular-Vorbelegung (priority=100, enabled=1, …)
  const strgDefaultsFor = useCallback((realTable) => {
    const out = {};
    for (const c of strgVisibleColumns(realTable)) {
      if (c.default !== null && c.default !== undefined && c.default !== "") {
        out[c.field] = String(c.default);
      }
    }
    return out;
  }, [strgVisibleColumns]);

  const SCHEMA_TO_TABLE = {
    versicherung: "versicherung",
    iban: "iban",
    mandat: "mandat",
    steuernummer: "steuernummer",
    vertrag: "vertrag",
  };
  // Welle 131: Stammdaten-Maske (DB 2) — Vorbelegung aus dem Dateinamen (Präfix, Absender, Nummern)
  const STAMM_KAT = { vertrag: "vertraege", versicherung: "versicherungen", iban: "iban", steuernummer: "steuernummer" };
  const [sdDialog, setSdDialog] = useState(null);
  const openStammdaten = useCallback((datei, startKat) => {
    const name = datei?.name || "";
    const nummern = (name.match(/[0-9A-Z]{4,}/gi) || []).filter((n) => /\d{4}/.test(n) && !/^20\d\d$/.test(n));
    setSdDialog({ datei, startKat: startKat || "vertraege",
      vorbelegung: { prefix: datei?.parsed?.prefix || "", absender: datei?.parsed?.absender || "", nummern } });
  }, []);
  const openStrgPopup = useCallback((filePath, defaultTable) => {
    setStrgFilePath(filePath);
    setStrgTable(defaultTable || "absender");
    setStrgForm({});
    setStrgMode("new");
    setStrgSearch("");
    setStrgEditId(null);
    setStrgEditOriginal(null);
    setStrgError(""); setStrgSuccess("");
    setStrgOpen(true);
  }, []);

  // Spalten der aktiven Tabelle laden, sobald Popup offen / Tabelle wechselt.
  useEffect(() => {
    if (strgOpen && strgRealTable) strgLoadColumns(strgRealTable);
  }, [strgOpen, strgRealTable, strgLoadColumns]);

  // Defaults vorbelegen, sobald Spalten da sind (nur im Neu-Modus, nur wenn
  // das Formular noch leer ist — User-Eingaben nie überschreiben).
  useEffect(() => {
    if (!strgOpen || strgMode !== "new") return;
    if (Object.keys(strgForm).length > 0) return;
    const defaults = strgDefaultsFor(strgRealTable);
    if (Object.keys(defaults).length > 0) setStrgForm(defaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strgOpen, strgMode, strgRealTable, strgColumns]);

  // Edit-Modus: Zeilen laden beim Umschalten / Tabellenwechsel.
  useEffect(() => {
    if (strgOpen && strgMode === "edit") strgLoadRows(strgRealTable);
  }, [strgOpen, strgMode, strgRealTable, strgLoadRows]);

  // Objekt-Adresse-Autofill: wenn objekt_id/objekt gesetzt wird und das
  // Formular ein objekt_adresse-Feld hat → Adresse aus `adressen` ziehen
  // (suchbegriff bevorzugt, sonst strasse).
  const strgAdressOptions = useMemo(() => {
    const objektVal = (strgForm.objekt_id || strgForm.objekt || "").trim();
    if (!objektVal || !adressenList) return [];
    const hits = adressenList.filter(a =>
      String(a.objekt_id || "").trim() === objektVal &&
      String(a.kategorie || "").toUpperCase() === "OBJEKT"
    );
    const rows = hits.length ? hits : adressenList.filter(a =>
      String(a.objekt_id || "").trim() === objektVal
    );
    const opts = [];
    for (const a of rows) {
      const v = (a.suchbegriff || "").trim() || (a.strasse || "").trim();
      if (v && !opts.includes(v)) opts.push(v);
    }
    return opts;
  }, [strgForm.objekt_id, strgForm.objekt, adressenList]);

  useEffect(() => {
    if (!strgOpen) return;
    const hasAdresseField = (strgColumns[strgRealTable] || [])
      .some(c => c.field === "objekt_adresse");
    if (!hasAdresseField) return;
    const objektVal = (strgForm.objekt_id || "").trim();
    if (!objektVal) return;
    strgLoadAdressen();
    // Genau 1 Treffer + Feld noch leer → automatisch befüllen.
    if (strgAdressOptions.length === 1 && !(strgForm.objekt_adresse || "").trim()) {
      setStrgForm(prev => ({ ...prev, objekt_adresse: strgAdressOptions[0] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strgOpen, strgForm.objekt_id, strgAdressOptions.length]);

  const openTrelloPopup = useCallback((filePath) => {
    setTrelloPopupPath(filePath);
    setTrelloGrund("");
    const filename = filePath.split("/").pop() || "";
    setTrelloTitel(cleanFilenameForTrello(filename));
    setTrelloError(""); setTrelloSuccess("");
    setTrelloPopupOpen(true);
  }, []);

  const trelloCreateCard = useCallback(async (filePath, mode, grund, titel) => {
    setTrelloSaving(true); setTrelloError(""); setTrelloSuccess("");
    try {
      const resp = await fetch(API_BASE + "/zuordnen/api/trello/create-card", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ quell_pfad: filePath, grund: grund || "", titel: titel || "" }),
      });
      if (!resp.ok) {
        let d = ""; try { const j = await resp.json(); d = j.detail || ""; } catch {}
        throw new Error("API " + resp.status + (d ? ": " + d : ""));
      }
      const res = await resp.json();
      setTrelloSuccess("Card erstellt: " + res.card_url);
      // trello_enabled im fileSettings setzen
      setFileSettings(prev => ({
        ...prev,
        [filePath]: { ...prev[filePath], trello_enabled: true, trello_mode: mode, trello_grund: grund || "" },
      }));
      return res;
    } catch (e) { setTrelloError(e.message); return null; }
    finally { setTrelloSaving(false); }
  }, []);

  /* == Hintergrund-Verschiebe-Jobs (Welle 28) ==
     Der Batch geht als Job an den Server, das UI bleibt frei. Ergebnisse
     kommen per Poll zurück: ok → Zeile entfernen, Fehler → Zeile bleibt und
     der Indikator zeigt den Fehler der richtigen Datei zu. */
  const selectedPathRef = useRef(null); // Pfad der fokussierten Datei — Fokus darf beim Entfernen anderer Zeilen nicht springen
  useEffect(() => { selectedPathRef.current = dateien[selectedIdx]?.path || null; }, [dateien, selectedIdx]);
  const onJobItemResult = useCallback((item) => {
    setInFlightPaths((prev) => { const n = new Set(prev); n.delete(item.quell_pfad); return n; });
    if (item.ok) {
      setCheckedPaths((prev) => { const n = new Set(prev); n.delete(item.quell_pfad); return n; });
      setDateien((prev) => {
        const selPath = selectedPathRef.current;
        const next = prev.filter((d) => d.path !== item.quell_pfad);
        if (selPath && selPath !== item.quell_pfad) {
          // Fokus auf der gerade bearbeiteten Datei halten (Index neu ermitteln)
          const ni = next.findIndex((d) => d.path === selPath);
          if (ni >= 0) { setSelectedIdx(ni); return next; }
        }
        setSelectedIdx((i) => Math.min(i, next.length - 1));
        return next;
      });
      releaseLock(item.quell_pfad);
    }
  }, [releaseLock]);

  const { jobs, startJob, dismissJob } = useVerschiebenJobs({
    pageKey: "dokumente",
    onItemResult: onJobItemResult,
  });

  /* Item für den Verschiebe-Job bauen (Felder wie /verschieben/ausfuehren
     + optionales trello-Objekt = Card beim Verschieben erstellen). */
  const buildJobItem = useCallback((datei, overrideZiel, kopienOverride) => {
    const s = fileSettings[datei.path] || {};
    const effZiel = overrideZiel
                  || (s.confidence === "manuell"     ? manuellOrdner
                  : s.confidence === "wiederholen" ? wiederholenOrdner
                  : s.ziel_ordner);
    const skipDb = s.confidence === "manuell" || s.confidence === "wiederholen";
    const item = {
      quell_pfad: datei.path, ziel_ordner: effZiel || "",
      kopie_datev: kopienOverride ? !!kopienOverride.kopie_datev : (s.kopie_datev || false),
      kopie_umbenannt: kopienOverride ? !!kopienOverride.kopie_umbenannt : (s.kopie_umbenannt || false),
      kopie_nok: s.kopie_nok || false, nok_original: s.nok_original || false,
      auto_verschieben: s.auto_verschieben || false, automatisch: false,
      bwa_split: s.bwa_split || false, skip_db: skipDb,
      duplikat_quelle: s.duplikat_quelle || false,
    };
    if (s.trello_enabled && s.trello_mode === "beim_verschieben") {
      // Prefix mitgeben, damit der Kartentitel mit „005:" beginnt (Welle 60).
      item.trello = {
        grund: s.trello_grund || "",
        titel: s.trello_titel || "",
        prefix: datei.parsed?.prefix || "",
      };
    }
    return item;
  }, [fileSettings, manuellOrdner, wiederholenOrdner]);

  /* Zeilen sofort ausgrauen / wieder aktivieren (z. B. während Duplikat-Prüfung). */
  const grauEin = useCallback((paths) => {
    setInFlightPaths((prev) => { const n = new Set(prev); paths.forEach((p) => n.add(p)); return n; });
  }, []);
  const grauAus = useCallback((paths) => {
    setInFlightPaths((prev) => { const n = new Set(prev); paths.forEach((p) => n.delete(p)); return n; });
  }, []);

  /* Items abschicken + Zeilen als „in Verschiebung" markieren. */
  const submitJob = useCallback(async (items) => {
    if (!items.length) return;
    try {
      await startJob(items);
      grauEin(items.map((it) => it.quell_pfad));
    } catch (e) {
      grauAus(items.map((it) => it.quell_pfad));
      setMoveResult({ ok: false, msg: `Verschieben konnte nicht gestartet werden: ${e.message}` });
    }
  }, [startJob, grauEin, grauAus]);

  const strgField = (key, label, placeholder) => (
    <label className="v-strg-field" key={key}>
      <span>{label}</span>
      <input value={strgForm[key] || ""} placeholder={placeholder || ""}
        onChange={(e) => setStrgForm(prev => ({ ...prev, [key]: e.target.value }))} />
    </label>
  );

  // Welle 20: rendert ein Formularfeld aus einer DESCRIBE-Spalte.
  // Typ-Erkennung: int/decimal → number, date → date, match_mode → Select.
  const strgDynField = (col) => {
    const key = col.field;
    const type = String(col.type || "").toLowerCase();
    const required = col.null === "NO" && (col.default === null || col.default === undefined);
    const label = strgPrettyLabel(key) + (required ? " *" : "");
    const common = {
      value: strgForm[key] ?? "",
      onChange: (e) => setStrgForm(prev => ({ ...prev, [key]: e.target.value })),
    };
    if (key === "match_mode") {
      return (
        <label className="v-strg-field" key={key}>
          <span>{label}</span>
          <select value={strgForm[key] || "word"}
            onChange={(e) => setStrgForm(prev => ({ ...prev, [key]: e.target.value }))}>
            <option value="word">word (ganzes Wort)</option>
            <option value="substr">substr (Teilstring)</option>
          </select>
        </label>
      );
    }
    if (key === "objekt_adresse" && strgAdressOptions.length > 1) {
      return (
        <label className="v-strg-field" key={key}>
          <span>{label}</span>
          <input list="strg-adresse-options" {...common} placeholder="aus adressen wählen…" />
          <datalist id="strg-adresse-options">
            {strgAdressOptions.map(o => <option key={o} value={o} />)}
          </datalist>
        </label>
      );
    }
    let inputType = "text";
    if (/^(int|tinyint|smallint|bigint|decimal|float|double)/.test(type)) inputType = "number";
    if (/^date($|\()/.test(type)) inputType = "date";
    if (/^datetime|^timestamp/.test(type)) inputType = "datetime-local";
    return (
      <label className="v-strg-field" key={key}>
        <span>{label}</span>
        <input type={inputType} {...common} placeholder={strgPlaceholder(type)} />
      </label>
    );
  };

  // Sucht client-seitig über ALLE Spaltenwerte (Substring, case-insensitiv).
  const strgFilteredRows = useMemo(() => {
    const q = strgSearch.trim().toLowerCase();
    if (!q) return strgRows.slice(0, 200);
    return strgRows.filter(r =>
      Object.values(r).some(v => v !== null && String(v).toLowerCase().includes(q))
    ).slice(0, 200);
  }, [strgRows, strgSearch]);

  // Kompakte Zeilen-Beschriftung für die Edit-Trefferliste:
  // die ersten 3 nicht-leeren sichtbaren Spalten.
  const strgRowLabel = useCallback((row) => {
    const cols = strgVisibleColumns(strgRealTable);
    const parts = [];
    for (const c of cols) {
      const v = row[c.field];
      if (v !== null && v !== undefined && String(v).trim() !== "") {
        parts.push(String(v));
        if (parts.length >= 3) break;
      }
    }
    return parts.join(" · ") || ("#" + row.id);
  }, [strgRealTable, strgVisibleColumns]);

  const strgSelectEditRow = useCallback((row) => {
    setStrgEditId(row.id);
    setStrgEditOriginal(row);
    const form = {};
    for (const c of strgVisibleColumns(strgRealTable)) {
      const v = row[c.field];
      form[c.field] = v === null || v === undefined ? "" : String(v);
    }
    setStrgForm(form);
    setStrgError(""); setStrgSuccess("");
  }, [strgRealTable, strgVisibleColumns]);
  const listRef = useRef(null);
  const cardRefs = useRef({});
  const browseTargetPath = useRef(null); // Welche Datei gerade im Browse-Modal bearbeitet wird
  const browseOriginal = useRef(null);   // Zielpfad vor dem Stöbern — für Abbrechen
  const duplikatReplaceRef = useRef(null); // Fokus auf 'Ersetzen'-Button im Duplikat-Modal
  const duplikatKopienRef = useRef({ datev: false, umbenannt: false }); // Kopien-Entscheidung beim Ersetzen
  const trelloBeimVerschiebenRef = useRef(null); // Fokus auf 'Card beim Verschieben'-Button
  const frozenPath = useRef(null); // B5c: Status-Änderung friert Position ein
  const handleMoveRef = useRef(null); // Ref für handleMove (vermeidet TDZ in useEffect)

  const selected = selectedIdx >= 0 && selectedIdx < dateien.length ? dateien[selectedIdx] : null;

  /* == Status-Override: speichert/lädt temporären User-Status == */
  const saveStatusOverride = useCallback((pfad, confidence) => {
    apiPost("/verschieben/status", { pfad, confidence }).catch((e) => console.warn("Status-Override speichern fehlgeschlagen:", e));
  }, []);

  const loadStatusOverrides = useCallback(async () => {
    try {
      const data = await api("/verschieben/status-overrides");
      return data.overrides || {};
    } catch { return {}; }
  }, []);

  /* == Gefilterte Dateiliste (Prefix + Datum + Freitext Multi-Term) == */
  const filteredDateien = useMemo(() => {
    return dateien.filter((d) => {
      const s = fileSettings[d.path] || {};
      // Prefix-Filter
      if (filterPrefix && !(d.parsed?.prefix || "").toLowerCase().includes(filterPrefix.toLowerCase())) return false;
      // Datum-Filter
      if (filterDatum) {
        const datum = d.parsed?.datum || "";
        if (!datum.includes(filterDatum)) return false;
      }
      // Freitext Multi-Term (alle Terme müssen matchen, AND-Verknüpfung)
      if (filterText) {
        const terms = filterText.toLowerCase().split(/\s+/).filter(Boolean);
        const haystack = [d.name, d.path, s.ziel_ordner || "", s.reason || "", d.parsed?.absender || "", d.parsed?.schema || ""].join(" ").toLowerCase();
        if (!terms.every(t => haystack.includes(t))) return false;
      }
      return true;
    });
  }, [dateien, fileSettings, filterPrefix, filterDatum, filterText]);

  // Vorhandene Präfixe (unique, sortiert) für das Filter-Dropdown
  const prefixOptions = useMemo(() => {
    const set = new Set();
    for (const d of dateien) {
      const p = (d.parsed?.prefix || "").trim();
      if (p && p !== "?") set.add(p);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, "de"));
  }, [dateien]);

  // Ordnersuche im Browse-Panel (debounced) — nutzt den Snapshot-Cache
  useEffect(() => {
    const q = ordnerSucheQuery.trim();
    if (q.length < 2) { setOrdnerSucheTreffer([]); return; }
    const t = setTimeout(async () => {
      try {
        const data = await api("/verschieben/ordner-suche?q=" + encodeURIComponent(q));
        setOrdnerSucheTreffer(data.treffer || []);
      } catch (e) { console.warn(e); }
    }, 300);
    return () => clearTimeout(t);
  }, [ordnerSucheQuery]);

  /* == Phase 1: Load file names only (no analysis) == */
  const loadDateien = useCallback(async (folder) => {
    setPhase("loading");
    setLoadMsg("Dateien laden...");
    setAnalysed(false);
    resetLocks();   // Welle 85: nur hier (und bei „Neuer Cache“) werden Sektionen neu vergeben
    const folderParam = folder || sourceFolder;
    const qs = folderParam ? "?ordner=" + encodeURIComponent(folderParam) : "";
    const signal = _neuerAbort();
    try {
      const data = await api("/verschieben/dateien" + qs, { signal });
      const files = data.dateien || [];
      setDateien(files);
      setOutbox(data.outbox || "");
      setManuellOrdner(data.manuell_ordner || "");
      setWiederholenOrdner(data.wiederholen_ordner || "");
      setSnapshotCount(0);
      // DB-Historie übernehmen (Pfad, Checkboxes, Auto)
      const settings = {};
      for (const d of files) {
        const isDuplikat = !!d.duplikat;
        const isAuto = !isDuplikat && ((d.auto?.eligible && d.auto?.ziel_ordner)
                    || (d.checkboxes?.auto_verschieben && d.vorschlag?.path));
        const autoZiel = d.auto?.ziel_ordner || d.vorschlag?.path || "";
        settings[d.path] = {
          ziel_ordner: isAuto ? autoZiel : (d.vorschlag?.path || ""),
          confidence: isDuplikat ? "duplikat" : (isAuto ? "auto" : (d.vorschlag?.confidence || "none")),
          reason: isDuplikat ? `Bereits vorhanden: ${d.duplikat.existing_name}` : (isAuto ? "Automatisch erkannt" : (d.vorschlag?.reason || "")),
          quelle: d.vorschlag?.quelle || "regel",
          kopie_datev: isDuplikat ? false : (d.checkboxes?.kopie_datev || false),
          kopie_umbenannt: isDuplikat ? false : (d.checkboxes?.kopie_umbenannt || false),
          auto_verschieben: isDuplikat ? false : (d.checkboxes?.auto_verschieben || false),
          bwa_split: false,
          trello_enabled: false,
          trello_mode: "beim_verschieben",
          trello_grund: "",
          auto: d.auto || { eligible: false },
          duplikat: d.duplikat || null,
        };
      }
      // DB-Overrides laden und mergen (B5e)
      const overrides = await loadStatusOverrides();
      for (const [pfad, conf] of Object.entries(overrides)) {
        if (settings[pfad]) settings[pfad].confidence = conf;
      }
      setFileSettings(settings);
      // checkedPaths bereinigen: nur noch vorhandene Dateien behalten
      const pathSet = new Set(files.map((f) => f.path));
      setCheckedPaths((prev) => {
        const n = new Set([...prev].filter((p) => pathSet.has(p)));
        return n.size === prev.size ? prev : n;
      });
      setSelectedIdx(files.length > 0 ? 0 : -1);
      setPhase("ready");
      // Analyse direkt im Hintergrund anstoßen — Liste ist sofort bedienbar
      runAnalyseRef.current?.();
    } catch (e) {
      if (_istAbbruch(e)) return; // Seite verlassen — kein Fehler
      setError(e.message);
      setPhase("error");
    }
  }, [sourceFolder, resetLocks]);

  useEffect(() => { loadDateien(); }, [loadDateien]);

  /* == Auto-Verschieben: per Button-Klick, nicht mehr automatisch == */

  /* == Phase 2: Analyse (on button click) == */
  const _applyAnalyseData = useCallback(async (data) => {
    const files = data.dateien || [];
    setOutbox(data.outbox || "");
    setManuellOrdner(data.manuell_ordner || "");
    setWiederholenOrdner(data.wiederholen_ordner || "");
    setSnapshotCount(data.snapshot_ordner || 0);
    setBerechnetAm(data.berechnet_am || null);
    setVonCache(data.von_cache !== false);
    // Scan-Status aus Response übernehmen
    if (data.scan_laeuft !== undefined) setScanLaeuft(data.scan_laeuft);
    const settings = {};
    for (const d of files) {
      const isDuplikat = !!d.duplikat;
      const isAuto = !isDuplikat && ((d.auto?.eligible && d.auto?.ziel_ordner)
                  || (d.checkboxes?.auto_verschieben && d.vorschlag?.path));
      const autoZiel = d.auto?.ziel_ordner || d.vorschlag?.path || "";
      settings[d.path] = {
        ziel_ordner: isAuto ? autoZiel : (d.vorschlag?.path || ""),
        confidence: isDuplikat ? "duplikat" : (isAuto ? "auto" : (d.vorschlag?.confidence || "red")),
        reason: isDuplikat ? `Bereits vorhanden: ${d.duplikat.existing_name}` : (isAuto ? "Automatisch erkannt" : (d.vorschlag?.reason || "")),
        quelle: d.vorschlag?.quelle || "regel",
        kopie_datev: isDuplikat ? false : (d.checkboxes?.kopie_datev || false),
        kopie_umbenannt: isDuplikat ? false : (d.checkboxes?.kopie_umbenannt || false),
        auto_verschieben: isDuplikat ? false : (d.checkboxes?.auto_verschieben || false),
        bwa_split: false,
        trello_enabled: false,
        trello_mode: "beim_verschieben",
        trello_grund: "",
        auto: d.auto || { eligible: false },
        duplikat: d.duplikat || null,
      };
    }
    // DB-Overrides laden und mergen (B5e)
    const overrides = await loadStatusOverrides();
    for (const [pfad, conf] of Object.entries(overrides)) {
      if (settings[pfad]) settings[pfad].confidence = conf;
    }
    // Welle 93: Was der Nutzer WÄHREND der laufenden Analyse eingestellt hat, gewinnt.
    // Vorher überschrieb das zurückkommende Ergebnis die Eingabe kommentarlos
    // (Lennard im Meeting: „jetzt hat er es nicht genommen — er war beim Analysieren").
    setFileSettings((prev) => {
      const zusammen = { ...settings };
      for (const [pfad, alt] of Object.entries(prev || {})) {
        if (!zusammen[pfad] || !alt?.ziel_manuell) continue;
        zusammen[pfad] = {
          ...zusammen[pfad],
          ziel_ordner: alt.ziel_ordner,
          ziel_manuell: true,
          confidence: alt.confidence ?? zusammen[pfad].confidence,
          reason: alt.reason || zusammen[pfad].reason,
        };
      }
      return zusammen;
    });
    // Nach Analyse: dateien in visueller Gruppen-Reihenfolge sortieren,
    // damit selectedIdx == visuelle Position und Sprünge nach Delete/Move ausbleiben
    const GROUP_RANK = { auto: 0, green: 1, yellow: 2, red: 3, duplikat: 4, manuell: 5, wiederholen: 6, none: 7, todo: 8 };
    const sortedFiles = [...files].sort((a, b) =>
      (GROUP_RANK[settings[a.path]?.confidence || "none"] ?? 7) -
      (GROUP_RANK[settings[b.path]?.confidence || "none"] ?? 7)
    );
    setDateien(sortedFiles);
    // Wenn Filter aktiv: auf ersten gefilterten Eintrag positionieren
    const firstIdx = filterText
      ? sortedFiles.findIndex((d) => d.name.toLowerCase().includes(filterText.toLowerCase()))
      : 0;
    setSelectedIdx(sortedFiles.length > 0 ? Math.max(firstIdx, 0) : -1);
    setAnalysed(true);
    setPhase("ready");
    // Welle 85: Sektionen einfrieren — nur Pfade, die noch kein Lock haben (bestehende bleiben stehen)
    lockMissing(sortedFiles.map((d) => [d.path, settings[d.path]?.confidence || "none"]));
  }, [filterText, loadStatusOverrides, lockMissing]);

  /* == Scan-Status Polling: wenn Scan läuft, alle 8s Status prüfen == */
  useEffect(() => {
    if (!scanLaeuft) {
      if (scanPollRef.current) { clearInterval(scanPollRef.current); scanPollRef.current = null; }
      return;
    }
    scanPollRef.current = setInterval(async () => {
      try {
        const status = await api("/verschieben/scan-status");
        if (!status.laeuft) {
          setScanLaeuft(false);
          setBerechnetAm(status.berechnet_am || null);
          setSnapshotCount(status.ordner || 0);
          setVonCache(false); // frischer Scan fertig
        }
      } catch {
        setScanLaeuft(false);
      }
    }, 8000);
    return () => { if (scanPollRef.current) { clearInterval(scanPollRef.current); scanPollRef.current = null; } };
  }, [scanLaeuft]);

  const runAnalyse = useCallback(async () => {
    // Läuft im Hintergrund: kein Ladebildschirm, Liste bleibt bedienbar,
    // der Analyse-Button zeigt solange „wird analysiert…".
    setAnalysing(true);
    const qs = sourceFolder ? "?ordner=" + encodeURIComponent(sourceFolder) : "";
    const signal = abortRef.current?.signal || _neuerAbort();
    try {
      const data = await api("/verschieben/alle" + qs, { signal });
      await _applyAnalyseData(data);
      // Fallback für Dateien ohne Ziel-Ordner: Einzel-Vorschlag nachholen
      const files = data.dateien || [];
      files.forEach((d) => {
        if (!d.vorschlag?.path && !d.auto?.ziel_ordner) {
          api("/verschieben/vorschlag?dateiname=" + encodeURIComponent(d.name), { signal })
            .then((res) => {
              if (res.path) {
                setFileSettings((prev) => {
                  const cur = prev[d.path];
                  if (!cur || cur.confidence === "manuell" || cur.confidence === "wiederholen") return prev;
                  if (cur.ziel_ordner) return prev; // wurde inzwischen gesetzt
                  const isAuto = res.auto?.eligible && res.auto?.ziel_ordner;
                  return {
                    ...prev,
                    [d.path]: {
                      ...cur,
                      ziel_ordner: isAuto ? res.auto.ziel_ordner : res.path,
                      confidence: isAuto ? "auto" : (res.confidence || "none"),
                      reason: res.reason || "",
                      quelle: res.quelle || "regel",
                      kopie_datev: res.checkboxes?.kopie_datev ?? cur.kopie_datev ?? false,
                      kopie_umbenannt: res.checkboxes?.kopie_umbenannt ?? cur.kopie_umbenannt ?? false,
                      auto_verschieben: res.checkboxes?.auto_verschieben ?? cur.auto_verschieben ?? false,
                    },
                  };
                });
              }
            })
            .catch(() => {});
        }
      });
    } catch (e) {
      if (!_istAbbruch(e)) {
        // Nicht-fatal: Liste bleibt stehen, Fehler als Toast
        setMoveResult({ ok: false, msg: `Analyse fehlgeschlagen: ${e.message}` });
      }
    } finally {
      setAnalysing(false);
    }
  }, [sourceFolder, _applyAnalyseData]);
  runAnalyseRef.current = runAnalyse;

  const runFreshAnalyse = useCallback(async () => {
    setAnalysing(true);
    resetLocks();   // Welle 85: „Neuer Cache“ = bewusst neue Sektionen
    const qs = sourceFolder ? "?ordner=" + encodeURIComponent(sourceFolder) : "";
    const signal = abortRef.current?.signal || _neuerAbort();
    try {
      const data = await fetch(API_BASE + "/zuordnen/api/verschieben/analyse-neu" + qs, {
        method: "POST",
        signal,
      }).then(async (r) => {
        if (!r.ok) {
          let detail = "";
          try { const j = await r.json(); detail = j.detail || ""; } catch {}
          throw new Error("API " + r.status + (detail ? ": " + detail : ""));
        }
        return r.json();
      });
      // Sofortiges Ergebnis anzeigen (aus Cache); Scan läuft ggf. im Hintergrund
      await _applyAnalyseData(data);
    } catch (e) {
      if (!_istAbbruch(e)) {
        setMoveResult({ ok: false, msg: `Analyse fehlgeschlagen: ${e.message}` });
      }
    } finally {
      setAnalysing(false);
    }
  }, [sourceFolder, _applyAnalyseData, resetLocks]);

  /* == PDF laden (pdf.js) == */
  useEffect(() => {
    if (!selected) { setPdfDoc(null); setPdfNumPages(0); setPdfPage(0); setThumbLoading(false); setThumbnail(null); return; }
    const path = selected.path;
    // Reset Viewer State (+ Editor-State — beim Dokumentwechsel wie nach dem Speichern)
    setPdfPage(0); setPdfZoom(1); setPdfRotation(0); setPdfPan({ x: 0, y: 0 });
    setEditMode(false); setEditAnnos([]); setEditFelder(null); setEditAktiveId(null); setEditLoeschen([]);
    // Aus Cache?
    if (pdfDocCache.current[path]) {
      const doc = pdfDocCache.current[path];
      setPdfDoc(doc); setPdfNumPages(doc.numPages); setThumbLoading(false); setThumbnail({ loaded: true });
      return;
    }
    setThumbLoading(true); setThumbnail(null); setPdfDoc(null);
    // Race-Condition-Schutz: falls der User schnell wechselt, soll kein
    // veralteter Ladevorgang das aktuelle Dokument überschreiben.
    let cancelled = false;
    (async () => {
      try {
        // pdf.js lazy laden
        if (!pdfjsRef.current) {
          let lib;
          try { lib = await import('pdfjs-dist/legacy/build/pdf.mjs'); } catch {
            try { lib = await import('pdfjs-dist/build/pdf.mjs'); } catch { lib = await import('pdfjs-dist'); }
          }
          const ver = lib?.version || '4.10.38';
          lib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${ver}/legacy/build/pdf.worker.min.mjs`;
          pdfjsRef.current = lib;
        }
        // PDF Bytes laden
        const resp = await fetch(API_BASE + "/zuordnen/api/verschieben/pdf-download?path=" + encodeURIComponent(path));
        if (!resp.ok) throw new Error("PDF download " + resp.status);
        const buf = await resp.arrayBuffer();
        if (cancelled) return; // Selektion hat sich geändert während wir geladen haben
        const doc = await pdfjsRef.current.getDocument({ data: buf }).promise;
        if (cancelled) return;
        pdfDocCache.current[path] = doc;
        setPdfDoc(doc); setPdfNumPages(doc.numPages); setThumbnail({ loaded: true });
      } catch (err) {
        if (!cancelled) { console.error("PDF load error", err); setThumbnail(null); setPdfDoc(null); }
      } finally { if (!cancelled) setThumbLoading(false); }
    })();
    return () => { cancelled = true; }; // Cleanup: veralteter Ladevorgang wird ignoriert
  }, [selected?.path, pdfReloadTick]); // path-basierte dep, nicht Objekt-Referenz; Tick = Reload nach PDF-Bearbeitung

  /* == PDF Seite rendern == */
  useEffect(() => {
    if (!pdfDoc || !pdfCanvasRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        if (pdfRenderTask.current) { try { pdfRenderTask.current.cancel(); } catch {} }
        const pg = await pdfDoc.getPage(pdfPage + 1);
        const canvas = pdfCanvasRef.current;
        if (!canvas || cancelled) return;
        const ctx = canvas.getContext('2d');
        const hostW = canvas.closest('.v-thumb-area')?.clientWidth || canvas.parentElement?.parentElement?.clientWidth || 600;
        const baseVp = pg.getViewport({ scale: 1, rotation: pdfRotation });
        const scaleW = hostW / baseVp.width;
        const cssScale = Math.max(0.2, pdfZoom) * scaleW;
        const vp = pg.getViewport({ scale: cssScale, rotation: pdfRotation });
        const dpr = window.devicePixelRatio || 1;
        canvas.style.width = Math.floor(vp.width) + 'px';
        canvas.style.height = Math.floor(vp.height) + 'px';
        canvas.width = Math.floor(vp.width * dpr);
        canvas.height = Math.floor(vp.height * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, vp.width, vp.height);
        setPdfVp(vp); // Edit-Layer rendert mit demselben Viewport (zoom-invariant)
        const task = pg.render({ canvasContext: ctx, viewport: vp });
        pdfRenderTask.current = task;
        await task.promise;
        // Text-Layer für Textauswahl (manuell, funktioniert in pdf.js v3+v4)
        const textLayerDiv = pdfTextLayerRef.current;
        if (textLayerDiv && !cancelled) {
          textLayerDiv.innerHTML = '';
          textLayerDiv.style.width = Math.floor(vp.width) + 'px';
          textLayerDiv.style.height = Math.floor(vp.height) + 'px';
          try {
            const textContent = await pg.getTextContent();
            if (!cancelled && pdfjsRef.current.Util) {
              const util = pdfjsRef.current.Util;
              for (const item of textContent.items) {
                if (!('str' in item) || !item.str) continue;
                const tx = util.transform(vp.transform, item.transform);
                // Schriftgröße aus vertikaler Skalierung (tx[3])
                const fontH = Math.abs(tx[3]);
                if (fontH < 1) continue;
                const angle = Math.atan2(tx[1], tx[0]);
                const span = document.createElement('span');
                span.textContent = item.str + (item.hasEOL ? ' ' : '');
                span.style.cssText = [
                  `position:absolute`,
                  `left:${tx[4]}px`,
                  `top:${tx[5] - fontH}px`,
                  `font-size:${fontH}px`,
                  `font-family:sans-serif`,
                  `transform-origin:0% 0%`,
                  angle !== 0 ? `transform:rotate(${angle}rad)` : '',
                  `white-space:pre`,
                  `color:transparent`,
                  `cursor:text`,
                  `user-select:text`,
                  `-webkit-user-select:text`,
                ].filter(Boolean).join(';');
                textLayerDiv.appendChild(span);
              }
            }
          } catch {/* Text-Layer optional */ }
        }
      } catch (e) { if (e?.name !== 'RenderingCancelledException') console.error('PDF render', e); }
    })();
    return () => { cancelled = true; };
  }, [pdfDoc, pdfPage, pdfZoom, pdfRotation]);

  /* == Keyboard == */
  useEffect(() => {
    const handler = (e) => {
      if (phase !== "ready" || filteredDateien.length === 0) return;
      if (browseOpen || editMode) return;   // Welle 91: keine Datei-Navigation im PDF-Editor
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      // Sichtbare Indizes (Position in originalem dateien-Array)
      const visIdx = filteredDateien.map((d) => dateien.indexOf(d));
      if (e.key === "ArrowDown" || e.key === "j") {
        e.preventDefault();
        const pos = visIdx.indexOf(selectedIdx);
        const next = visIdx[Math.min(pos === -1 ? 0 : pos + 1, visIdx.length - 1)];
        setSelectedIdx(next ?? visIdx[0]);
        setMoveResult(null);
      } else if (e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        const pos = visIdx.indexOf(selectedIdx);
        const prev = visIdx[Math.max(pos === -1 ? 0 : pos - 1, 0)];
        setSelectedIdx(prev ?? visIdx[0]);
        setMoveResult(null);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const sel = dateien[selectedIdx];
        if (sel) {
          // Enter = Checkbox togglen + zur nächsten Datei springen
          setCheckedPaths((prev) => {
            const n = new Set(prev);
            if (n.has(sel.path)) n.delete(sel.path); else n.add(sel.path);
            return n;
          });
          // Zur nächsten sichtbaren Datei springen
          const pos = visIdx.indexOf(selectedIdx);
          const next = visIdx[Math.min(pos + 1, visIdx.length - 1)];
          if (next !== undefined && next !== selectedIdx) setSelectedIdx(next);
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [phase, filteredDateien, dateien, browseOpen, editMode, selectedIdx, fileSettings]);

  /* == PDF-Editor: Start / Speichern / Abbrechen (Welle 91) == */
  const editorHatAenderungen = () =>
    editAnnos.length > 0 || editLoeschen.length > 0 || Object.values(editFelder || {}).some((f) => f.geaendert);

  const editorStart = async () => {
    if (!selected) return;
    setPdfRotation(0);            // Editor arbeitet ohne Ansichts-Rotation (Koordinaten einfach)
    setEditTool("text"); setEditAnnos([]); setEditAktiveId(null); setEditLoeschen([]);
    setEditMode(true);
    try {
      const d = await api("/verschieben/pdf-formfelder?path=" + encodeURIComponent(selected.path));
      const map = {};
      (d.felder || []).forEach((f) => { map[f.name] = { ...f, wert: f.wert ?? "", geaendert: false }; });
      setEditFelder(map);
    } catch { setEditFelder({}); }
  };

  const editorAbbrechen = () => {
    if (editorHatAenderungen() && !window.confirm("Änderungen verwerfen?")) return;
    setEditMode(false); setEditAnnos([]); setEditFelder(null); setEditAktiveId(null); setEditLoeschen([]);
  };

  const editorSpeichern = async () => {
    if (!selected) return;
    setEditSaving(true);
    try {
      const annotationen = editAnnos
        .filter((a) => a.typ !== "text" || a.text.trim())
        .map(({ id, ...rest }) => rest);
      const felder = {};
      Object.entries(editFelder || {}).forEach(([n, f]) => { if (f.geaendert) felder[n] = String(f.wert ?? ""); });
      if (editLoeschen.length >= pdfNumPages) throw new Error("Es muss mindestens eine Seite übrig bleiben.");
      await apiPost("/verschieben/pdf-bearbeiten", { pfad: selected.path, annotationen, felder,
        seiten_loeschen: editLoeschen });
      if (editLoeschen.length) setPdfPage(0);
      setEditMode(false); setEditAnnos([]); setEditFelder(null); setEditAktiveId(null); setEditLoeschen([]);
      try { delete pdfDocCache.current[selected.path]; } catch {}
      setPdfReloadTick((t) => t + 1);   // Viewer lädt den gespeicherten Stand
    } catch (e) {
      alert("PDF speichern fehlgeschlagen: " + e.message);
    } finally {
      setEditSaving(false);
    }
  };

  // Welle 136: zweites Dokument hinten anhängen; es wandert danach in den Dropbox-Papierkorb
  const vereinenAusfuehren = async () => {
    if (!selected || !vereinenMit) return;
    setVereinenLaeuft(true);
    try {
      const mit = vereinenMit.path;
      await apiPost("/verschieben/pdf-vereinen", { pfad: selected.path, mit_pfad: mit });
      setVereinenOffen(false); setVereinenMit(null); setVereinenSuche("");
      setEditMode(false); setEditAnnos([]); setEditFelder(null); setEditAktiveId(null); setEditLoeschen([]);
      try { delete pdfDocCache.current[selected.path]; delete pdfDocCache.current[mit]; } catch {}
      // Auswahl hängt an der Listenposition: lag das zweite Dokument davor, rückt das bearbeitete eins nach oben
      const idxMit = dateien.findIndex((d) => d.path === mit);
      setDateien((prev) => prev.filter((d) => d.path !== mit));
      if (idxMit >= 0 && idxMit < selectedIdx) setSelectedIdx((i) => i - 1);
      setPdfReloadTick((t) => t + 1);
    } catch (e) {
      alert("Vereinen fehlgeschlagen: " + e.message);
    } finally {
      setVereinenLaeuft(false);
    }
  };

  useEffect(() => {
    if (selectedIdx >= 0 && cardRefs.current[selectedIdx]) {
      cardRefs.current[selectedIdx].scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
    // B5c: Freeze freigeben wenn User auf andere Datei wechselt
    // → erst jetzt lockedGroups aktualisieren, damit Datei in neue Gruppe springt
    const sel = dateien[selectedIdx];
    if (frozenPath.current && sel && sel.path !== frozenPath.current) {
      const fp = frozenPath.current;
      frozenPath.current = null;
      const newConf = fileSettings[fp]?.confidence;
      if (newConf && lockedRef.current[fp] !== newConf) { lockedRef.current[fp] = newConf; _syncLocks(); }
    }
  }, [selectedIdx, fileSettings]);

  /* == Duplikat-Modal: Fokus auf Ersetzen-Button == */
  useEffect(() => {
    if (duplikatInfo) setTimeout(() => duplikatReplaceRef.current?.focus(), 60);
  }, [duplikatInfo]);

  /* == Trello-Modal: Fokus auf 'Card beim Verschieben'-Button == */
  useEffect(() => {
    if (trelloPopupOpen) setTimeout(() => trelloBeimVerschiebenRef.current?.focus(), 60);
  }, [trelloPopupOpen]);

  /* == Helpers == */
  const shortPath = (p) => {
    if (!p) return "";
    const idx = p.indexOf("Holthey Immobilien/");
    return idx >= 0 ? p.substring(idx + 19) : p;
  };

  /* == Source Folder Browse == */
  const browseSrcFolder = useCallback(async (path) => {
    setSrcBrowseLoading(true);
    try {
      const data = await api("/verschieben/browse-ordner?path=" + encodeURIComponent(path));
      setSrcBrowseItems(data.ordner || []);
      setSrcBrowsePath(path);
    } catch (e) { console.warn(e); }
    finally { setSrcBrowseLoading(false); }
  }, []);

  const openSrcBrowse = useCallback(() => {
    setSrcBrowseOpen(true);
    browseSrcFolder(outbox || "/Holthey Immobilien");
  }, [outbox, browseSrcFolder]);

  const pickSrcFolder = useCallback((folderPath) => {
    setSourceFolder(folderPath);
    setSrcBrowseOpen(false);
    loadDateien(folderPath);
  }, [loadDateien]);

  const resetSrcFolder = useCallback(() => {
    setSourceFolder("");
    loadDateien("");
  }, [loadDateien]);

  /* == Einzel-Datei Vorschlag neu laden == */
  const analyseEinzel = useCallback(async (datei) => {
    setAnalysingPath(datei.path);
    try {
      const res = await api("/verschieben/vorschlag?dateiname=" + encodeURIComponent(datei.name));
      if (res.path) {
        setFileSettings((prev) => {
          const cur = prev[datei.path] || {};
          if (cur.confidence === "manuell" || cur.confidence === "wiederholen") return prev;
          const isAuto = res.auto?.eligible && res.auto?.ziel_ordner;
          return {
            ...prev,
            [datei.path]: {
              ...cur,
              ziel_ordner: isAuto ? res.auto.ziel_ordner : res.path,
              confidence: isAuto ? "auto" : (res.confidence || "none"),
              reason: res.reason || "",
              quelle: res.quelle || "regel",
              kopie_datev: res.checkboxes?.kopie_datev ?? cur.kopie_datev ?? false,
              kopie_umbenannt: res.checkboxes?.kopie_umbenannt ?? cur.kopie_umbenannt ?? false,
              auto_verschieben: res.checkboxes?.auto_verschieben ?? cur.auto_verschieben ?? false,
              auto: res.auto || cur.auto || { eligible: false },
            },
          };
        });
      }
    } catch (e) {
      console.warn("Einzel-Vorschlag fehlgeschlagen:", e);
    } finally {
      setAnalysingPath(null);
    }
  }, []);

  /* == Welle 79: Namen einer Pipeline übernehmen (Alternative in der Karte) ==
     Umbenennen (mit Quelle fürs Pipeline-Log) → Datei in-place patchen → Ordner/Ampel
     neu vom Backend holen → Karte in die Gruppe der frischen Ampel. */
  const uebernehmeNamen = useCallback(async (datei, neuerName, quelle, sessionId = null, proposed = null) => {
    if (!datei || !neuerName) return;
    let newName = neuerName.toLowerCase().endsWith(".pdf") ? neuerName : neuerName + ".pdf";
    const oldPath = datei.path;
    const parentDir = oldPath.replace(/\/[^/]+$/, "");
    let newPath = `${parentDir}/${newName}`;
    setReprocessingPath(oldPath);
    try {
      if (newName !== datei.name) {
        const res = await apiPost("/verschieben/umbenennen", { alter_pfad: oldPath, neuer_name: newName, quelle, session_id: sessionId, proposed: proposed || neuerName });
        // Name schon vergeben → Backend hängt „ (1)“ an; tatsächlichen Namen übernehmen
        if (res?.neuer_pfad) { newPath = res.neuer_pfad; newName = res.neuer_name; }
      }
      setDateien(prev => prev.map(d => d.path === oldPath
        ? { ...d, path: newPath, name: newName,
            alternativen: (d.alternativen || []).filter(a => a.name !== neuerName) }
        : d));
      setFileSettings(prev => {
        const { [oldPath]: oldSett, ...rest } = prev;
        // todo-Flag überlebt das Umbenennen (Backend zieht den Override mit um)
        const confNeu = oldSett?.confidence === "todo" ? "todo" : "none";
        return { ...rest, [newPath]: { ...(oldSett || {}), confidence: confNeu,
          reason: `${quelle === "v1_batch" ? "v1" : "Gemini"}-Name übernommen → ${newName}` } };
      });
      moveLock(oldPath, newPath);   // Welle 85: Sektion bleibt, nur Name/Farbe ändern sich
      try { delete pdfDocCache.current[oldPath]; } catch {}
      await analyseEinzel({ path: newPath, name: newName });
      lockSection(newPath, fileSettingsRef.current?.[newPath]?.confidence);
    } catch (e) {
      alert("Umbenennen fehlgeschlagen: " + e.message);
    } finally {
      setReprocessingPath(null);
    }
  }, [analyseEinzel, moveLock, lockSection]);

  /* == Reprocess: PDF nochmal durch OCR/Rename-Pipeline == */
  const reprocessFile = useCallback(async (datei, pipeline = "v1", schemaOverride = "") => {
    setReprocessingPath(datei.path);
    try {
      let url = API_BASE + "/zuordnen/api/verschieben/reprocess?path=" + encodeURIComponent(datei.path) + "&pipeline=" + encodeURIComponent(pipeline);
      if (schemaOverride && schemaOverride.trim()) {
        url += "&schema_override=" + encodeURIComponent(schemaOverride.trim());
      }
      const res = await fetch(url, { method: "POST" })
        .then(async (r) => { if (!r.ok) { let d = ""; try { const j = await r.json(); d = j.detail || ""; } catch {} throw new Error("API " + r.status + (d ? ": " + d : "")); } return r.json(); });
      if (res.status === "ok" && res.proposed) {
        // Neuen Namen anwenden falls anders
        const oldName = datei.name;
        let newName = res.proposed.endsWith(".pdf") ? res.proposed : res.proposed + ".pdf";
        // Pfad berechnen (ggf. neuer Name durch Umbenennen)
        const parentDir = datei.path.replace(/\/[^/]+$/, "");
        let newPath = `${parentDir}/${newName}`;
        if (newName !== oldName) {
          // In Dropbox umbenennen
          try {
            const ren = await apiPost("/verschieben/umbenennen", { alter_pfad: datei.path, neuer_name: newName,
              quelle: "v1_reprocess", proposed: res.proposed });
            if (ren?.neuer_pfad) { newPath = ren.neuer_pfad; newName = ren.neuer_name; }
          } catch (e) { console.warn("Umbenennen nach Reprocess fehlgeschlagen:", e); }
        }
        // dateien[] und fileSettings in-place aktualisieren — kein kompletter Reload
        setDateien(prev => prev.map(d => d.path === datei.path ? { ...d, name: newName, path: newPath } : d));
        // Welle 78: Ampel NICHT hart setzen und die alte Gruppe NICHT mitnehmen —
        // analyseEinzel liefert Ordner + Ampel für den neuen Namen, die Karte landet
        // in der Gruppe, die das Backend sagt.
        setFileSettings(prev => {
          const { [datei.path]: oldSett, ...rest } = prev;
          const confNeu = oldSett?.confidence === "todo" ? "todo" : "none";
          return { ...rest, [newPath]: { ...(oldSett || {}), confidence: confNeu, reason: `Reprocess: ${res.schema || "?"} → ${newName}` + (res.absender ? ` (${res.absender})` : "") } };
        });
        moveLock(datei.path, newPath);   // Welle 85: Sektion bleibt
        // PDF-Cache invalidieren
        delete pdfDocCache.current[datei.path];
        // Auto-Lupe: neuen Zielordner-Vorschlag auf Basis des neuen Dateinamens holen
        await analyseEinzel({ path: newPath, name: newName });
        lockSection(newPath, fileSettingsRef.current?.[newPath]?.confidence);
      }
    } catch (e) {
      console.warn("Reprocess fehlgeschlagen:", e);
    } finally {
      setReprocessingPath(null);
    }
  }, [analyseEinzel, moveLock, lockSection]);

  /* == Pipeline 2 — Claude Haiku / Gemma 4 KI-Analyse == */
  const startKi2 = useCallback(async (datei, model = "claude") => {
    setPipelinePickerFile(null);
    setKi2File(datei);
    setKi2Model(model);
    setKi2Result(null);
    setKi2SessionId(null);
    setKi2Polling(true);
    try {
      const res = await fetch(API_BASE + "/zuordnen/api/verschieben/ki-claude/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dropbox_path: datei.path, model }),
      });
      if (!res.ok) throw new Error("Start fehlgeschlagen: " + res.status);
      const data = await res.json();
      const sid = data.session_id;
      setKi2SessionId(sid);

      // Polling alle 3s bis result.json fertig
      ki2PollRef.current = setInterval(async () => {
        try {
          const pr = await fetch(API_BASE + "/zuordnen/api/verschieben/ki-claude/result/" + sid);
          if (!pr.ok) return;
          const pd = await pr.json();
          if (pd.status === "done" || pd.status === "error") {
            clearInterval(ki2PollRef.current);
            setKi2Polling(false);
            setKi2Result(pd);
          }
        } catch (e) {
          console.warn("Ki2 Poll Fehler:", e);
        }
      }, 3000);
    } catch (e) {
      console.warn("Pipeline 2 Start fehlgeschlagen:", e);
      setKi2Polling(false);
    }
  }, []);

  // Cleanup Poll bei Unmount
  React.useEffect(() => {
    return () => { if (ki2PollRef.current) clearInterval(ki2PollRef.current); };
  }, []);
  const moveNokOriginal = useCallback(async (datei) => {
    // Läuft wie jedes Verschieben als Hintergrund-Job (einheitliche Pipeline)
    setMoveResult(null);
    delete pdfDocCache.current[datei.path];
    await submitJob([{
      quell_pfad: datei.path,
      ziel_ordner: "",
      nok_original: true,
      kopie_nok: true,
      kopie_datev: false,
      kopie_umbenannt: false,
      auto_verschieben: false,
      automatisch: false,
      bwa_split: false,
      skip_db: true,
    }]);
  }, [submitJob]);

  const strgSave = useCallback(async (andReprocess) => {
    setStrgSaving(true); setStrgError(""); setStrgSuccess("");
    try {
      // Pflichtfelder prüfen (Null=NO ohne Default).
      for (const c of strgVisibleColumns(strgRealTable)) {
        const required = c.null === "NO" && (c.default === null || c.default === undefined);
        if (required && !(strgForm[c.field] || "").toString().trim()) {
          throw new Error(`${c.field} ist ein Pflichtfeld`);
        }
      }

      if (strgMode === "edit") {
        if (!strgEditId) throw new Error("Kein Eintrag ausgewählt");
        // Nur geänderte Felder senden.
        const changes = {};
        for (const c of strgVisibleColumns(strgRealTable)) {
          const orig = strgEditOriginal?.[c.field];
          const origStr = orig === null || orig === undefined ? "" : String(orig);
          const now = (strgForm[c.field] ?? "").toString();
          if (now !== origStr) changes[c.field] = now === "" ? null : now;
        }
        if (Object.keys(changes).length === 0) {
          setStrgSuccess("Keine Änderungen.");
          setStrgSaving(false);
          return;
        }
        const resp = await fetch(API_BASE + "/database/table/" + strgRealTable + "/row", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ pk_column: "id", pk_value: strgEditId, changes }),
        });
        if (!resp.ok) { let d = ""; try { const j = await resp.json(); d = j.detail || ""; } catch {} throw new Error("API " + resp.status + (d ? ": " + d : "")); }
        setStrgSuccess("Aktualisiert!");
        strgLoadRows(strgRealTable);
      } else {
        // Neu: nur nicht-leere Felder senden — DB-Defaults greifen für den Rest.
        const data = {};
        for (const c of strgVisibleColumns(strgRealTable)) {
          const v = (strgForm[c.field] ?? "").toString().trim();
          if (v !== "") data[c.field] = v;
        }
        if (Object.keys(data).length === 0) throw new Error("Keine Felder ausgefüllt");
        const resp = await fetch(API_BASE + "/database/table/" + strgRealTable + "/row", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ data }),
        });
        if (!resp.ok) { let d = ""; try { const j = await resp.json(); d = j.detail || ""; } catch {} throw new Error("API " + resp.status + (d ? ": " + d : "")); }
        setStrgSuccess("Gespeichert!");
      }
      if (andReprocess && strgFilePath) {
        const datei = dateien.find(dd => dd.path === strgFilePath);
        if (datei) { setStrgOpen(false); reprocessFile(datei); }
      }
    } catch (e) { setStrgError(e.message); }
    finally { setStrgSaving(false); }
  }, [strgRealTable, strgForm, strgMode, strgEditId, strgEditOriginal,
      strgVisibleColumns, strgLoadRows, strgFilePath, dateien, reprocessFile]);

  /* == Datei umbenennen == */
  const handleRename = useCallback(async (alterPfad, neuerName) => {
    const trimmed = neuerName.trim();
    if (!trimmed || trimmed === alterPfad.split("/").pop()) {
      setEditingPath(null);
      return;
    }
    try {
      const res = await apiPost("/verschieben/umbenennen", { alter_pfad: alterPfad, neuer_name: trimmed });
      // Dateien-Liste aktualisieren
      setDateien((prev) => prev.map((d) => d.path === alterPfad
        ? { ...d, name: res.neuer_name, path: res.neuer_pfad }
        : d
      ));
      // FileSettings-Key updaten — alter Ordner-Vorschlag ist mit dem neuen
      // Namen überholt und wird zurückgesetzt (Checkboxen bleiben erhalten)
      setFileSettings((prev) => {
        const s = prev[alterPfad] || {};
        const next = { ...prev };
        delete next[alterPfad];
        next[res.neuer_pfad] = { ...s, ziel_ordner: null, confidence: "none", reason: "" };
        return next;
      });
      moveLock(alterPfad, res.neuer_pfad);   // Welle 85: Sektion bleibt beim Umbenennen
      // Ordner-Suche für den neuen Namen automatisch neu anstoßen
      analyseEinzel({ path: res.neuer_pfad, name: res.neuer_name });
    } catch (e) {
      alert("Umbenennen fehlgeschlagen: " + e.message);
    } finally {
      setEditingPath(null);
    }
  }, [analyseEinzel, moveLock]);

  const createFolder = useCallback(async (parentPath, name, refreshFn) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const pfad = parentPath.replace(/\/$/, "") + "/" + trimmed;
    try {
      await apiPost("/verschieben/ordner-erstellen", { pfad });
      // Welle 93: direkt in den neuen Ordner hinein — vorher landete man im
      // Elternordner und musste erst hineinklicken, dann noch „Hier ablegen" drücken.
      await refreshFn(pfad);
    } catch (e) {
      alert("Fehler beim Erstellen: " + e.message);
    }
  }, []);

  const toggleSetting = useCallback((path, key) => {
    setFileSettings((prev) => ({
      ...prev,
      [path]: { ...prev[path], [key]: !prev[path]?.[key] },
    }));
  }, []);

  const updateZiel = useCallback((path, ziel) => {
    setFileSettings((prev) => ({
      ...prev,
      [path]: { ...prev[path], ziel_ordner: ziel, ziel_manuell: true,
                confidence: "green", reason: "Manuell gewählt" },
    }));
  }, []);

  // Beim Tippen im Ziel-Feld: NUR den Pfad ändern, KEIN confidence:"green"
  // (sonst springt die Karte in die Gruppe „Sicher"). Debounce/Isolierung
  // erledigt die memoisierte ZielInput-Komponente.
  const updateZielTyping = useCallback((path, ziel) => {
    setFileSettings((prev) => ({
      ...prev,
      [path]: { ...prev[path], ziel_ordner: ziel, ziel_manuell: true },
    }));
  }, []);

  // Datei sofort im Quellordner duplizieren (ohne Verschieben) → manuell umbenennbar
  const duplizierenJetzt = useCallback(async (pfad) => {
    try {
      const res = await apiPost("/verschieben/duplizieren", { pfad });
      setDuplikatPickerFile(null);
      await loadDateien();
      return res;
    } catch (e) {
      alert("Duplizieren fehlgeschlagen: " + e.message);
    }
  }, [loadDateien]);

  /* == Browse ==
     Welle 93: Der Zielpfad unten wandert beim Navigieren LIVE mit — „Hier ablegen"
     zu vergessen war die häufigste Fehlerquelle (Lennard: „ich vergesse das jedes Mal").
     Bewusst über updateZielTyping: das setzt nur den Pfad, nicht die Ampel — sonst
     springt die Karte beim Durchklicken zwischen den Gruppen. Beim Abbrechen (✕)
     wird der ursprüngliche Vorschlag wiederhergestellt. */
  const browseFolder = useCallback(async (path) => {
    setBrowseLoading(true);
    try {
      const data = await api("/verschieben/browse-ordner?path=" + encodeURIComponent(path));
      setBrowseItems(data.ordner || []);
      setBrowsePath(path);
      const ziel = browseTargetPath.current;
      if (ziel) updateZielTyping(ziel, path);
    } catch (e) { console.warn(e); }
    finally { setBrowseLoading(false); }
  }, [updateZielTyping]);

  const openBrowse = useCallback((targetDatei) => {
    const datei = targetDatei || selected;
    if (!datei) return;
    const s = fileSettings[datei.path];
    const startPath = s?.ziel_ordner || outbox;
    browseTargetPath.current = datei.path;
    browseOriginal.current = { path: datei.path, ziel: s?.ziel_ordner ?? "" };
    setBrowseOpen(true);
    setBrowsePath(startPath); // sofort anzeigen, kein leeres Flackern
    setNewFolderMode(false);
    setNewFolderName("");
    setOrdnerSucheQuery("");
    setOrdnerSucheTreffer([]);
    browseFolder(startPath);
  }, [selected, fileSettings, outbox, browseFolder]);

  const pickFolder = useCallback((folderPath) => {
    const path = browseTargetPath.current || selected?.path;
    if (path) updateZiel(path, folderPath);   // hier bewusst MIT Ampel auf grün
    browseOriginal.current = null;
    setBrowseOpen(false);
  }, [selected, updateZiel]);

  // Abbrechen: zurück auf den Stand vor dem Stöbern
  const browseAbbrechen = useCallback(() => {
    const orig = browseOriginal.current;
    if (orig?.path) updateZielTyping(orig.path, orig.ziel);
    browseOriginal.current = null;
    setBrowseOpen(false);
  }, [updateZielTyping]);

  /* == Move == */
  const handleDelete = useCallback(async (pfad) => {
    try {
      await apiPost("/verschieben/loeschen", { pfad });
      setCheckedPaths((prev) => { const n = new Set(prev); n.delete(pfad); return n; });
      setDateien((prev) => {
        const next = prev.filter((d) => d.path !== pfad);
        setSelectedIdx((i) => Math.min(i, next.length - 1));
        return next;
      });
      releaseLock(pfad);
    } catch (e) {
      setMoveResult({ ok: false, msg: e.message });
    } finally {
      setConfirmDeletePath(null);
    }
  }, []);

  /* == Duplikat-Check Helper == */
  const checkDuplikat = useCallback(async (quellPfad, zielOrdner) => {
    try {
      const res = await apiPost("/verschieben/duplikat-check", { quell_pfad: quellPfad, ziel_ordner: zielOrdner });
      if (res.duplikat) return res;
    } catch (e) { console.warn("Duplikat-Check fehlgeschlagen:", e); }
    return null;
  }, []);

  const checkBatchDuplikate = useCallback(async (items) => {
    const found = [];
    for (const item of items) {
      try {
        const res = await apiPost("/verschieben/duplikat-check", { quell_pfad: item.quell_pfad, ziel_ordner: item.ziel_ordner });
        if (res.duplikat) found.push({ quell: item, existierend: res.existierende_datei });
      } catch (e) { /* ignore */ }
    }
    return found;
  }, []);

  /* == _doMove: eigentliches Verschieben (ohne Duplikat-Check) ==
     kopienOverride: {kopie_datev, kopie_umbenannt} aus dem Duplikat-Popup —
     überschreibt die (dort deaktivierten) Checkbox-Werte. */
  const _doMove = useCallback(async (datei, overrideZiel, kopienOverride) => {
    // Verschieben läuft als Hintergrund-Job: Zeile wird als „in Verschiebung"
    // markiert und verschwindet, sobald das Job-Ergebnis ok meldet.
    setMoveResult(null);
    await submitJob([buildJobItem(datei, overrideZiel, kopienOverride)]);
  }, [buildJobItem, submitJob]);

  const handleMove = useCallback(async (targetDatei) => {
    const datei = targetDatei || selected;
    if (!datei) return;
    const s = fileSettings[datei.path];
    const effZiel = s?.confidence === "manuell"     ? manuellOrdner
                  : s?.confidence === "wiederholen" ? wiederholenOrdner
                  : s?.ziel_ordner;
    if (!effZiel) { setMoveResult({ ok: false, msg: "Kein Ziel-Ordner" }); return; }

    // Bei nok_original: direkt verschieben, kein Duplikat-Check nötig
    if (s?.nok_original) { await _doMove(datei); return; }

    // Duplikat-Check VOR dem Verschieben
    setDuplikatChecking(true);
    // Wenn bereits als Duplikat markiert, sofort Popup zeigen
    let dup = null;
    if (s?.confidence === "duplikat" && s?.duplikat) {
      dup = {
        duplikat: true,
        quell_datei: { size: datei.size },
        existierende_datei: {
          name: s.duplikat.existing_name,
          path: s.duplikat.existing_path,
          size: s.duplikat.existing_size,
          modified: s.duplikat.existing_modified,
        },
      };
    } else {
      dup = await checkDuplikat(datei.path, effZiel);
    }
    setDuplikatChecking(false);

    if (dup) {
      // Duplikat erkannt → kopie_datev + kopie_umbenannt automatisch deaktivieren.
      // Original-Wunsch merken: beim Ersetzen wird geprüft, ob die Kopie schon
      // existiert (vom ersten Move) — sonst entscheidet der User im Popup.
      const kopienWollte = { datev: !!s?.kopie_datev, umbenannt: !!s?.kopie_umbenannt };
      duplikatKopienRef.current = { datev: false, umbenannt: false };
      setFileSettings((prev) => ({
        ...prev,
        [datei.path]: { ...prev[datei.path], kopie_datev: false, kopie_umbenannt: false },
      }));
      const kopienOverride = () => ({
        kopie_datev: duplikatKopienRef.current.datev,
        kopie_umbenannt: duplikatKopienRef.current.umbenannt,
      });
      setDuplikatInfo({
        quell: { name: datei.name || datei.path.split("/").pop(), path: datei.path, size: dup.quell_datei?.size || datei.size, modified: dup.quell_datei?.modified || datei.modified },
        existierend: dup.existierende_datei,
        zielOrdner: effZiel,
        kopienWollte,
        kopienStatus: (kopienWollte.datev || kopienWollte.umbenannt) ? null : { fertig: true },
        onReplace: async () => {
          // Alte löschen + Neue in Ordner der alten Datei (ersetzen)
          const altOrdner = dup.existierende_datei.path ? dup.existierende_datei.path.replace(/\/[^/]+$/, "") : effZiel;
          try {
            await apiPost("/verschieben/loeschen", { pfad: dup.existierende_datei.path });
          } catch (e) {
            setMoveResult({ ok: false, msg: "Alte Datei löschen fehlgeschlagen: " + e.message });
            setDuplikatInfo(null);
            return;
          }
          setDuplikatInfo(null);
          await _doMove(datei, altOrdner, kopienOverride());
        },
        onReplaceNew: async () => {
          // Alte löschen + Neue in vorgeschlagenen Zielordner
          try {
            await apiPost("/verschieben/loeschen", { pfad: dup.existierende_datei.path });
          } catch (e) {
            setMoveResult({ ok: false, msg: "Alte Datei löschen fehlgeschlagen: " + e.message });
            setDuplikatInfo(null);
            return;
          }
          setDuplikatInfo(null);
          await _doMove(datei, effZiel, kopienOverride());
        },
        onDelete: async () => {
          // Quelldatei löschen (neue Datei, ist Duplikat)
          try {
            await apiPost("/verschieben/loeschen", { pfad: datei.path });
            setDateien((prev) => {
              const next = prev.filter((d) => d.path !== datei.path);
              setSelectedIdx((i) => Math.min(i, next.length - 1));
              return next;
            });
            setMoveResult({ ok: true, msg: "Duplikat gelöscht" });
          } catch (e) {
            setMoveResult({ ok: false, msg: "Löschen fehlgeschlagen: " + e.message });
          }
          setDuplikatInfo(null);
        },
        onKeepBoth: async () => {
          // Beide behalten: Neue in Zielordner (ohne alte zu löschen)
          setDuplikatInfo(null);
          await _doMove(datei, effZiel);
        },
        onAbort: () => setDuplikatInfo(null),
      });
      // Kopien-Check: liegt die Datei schon im Datev-/Umbenannt-Ordner?
      if (kopienWollte.datev || kopienWollte.umbenannt) {
        apiPost("/verschieben/kopie-check", { dateiname: datei.name || datei.path.split("/").pop() })
          .then((res) => {
            duplikatKopienRef.current = {
              datev: kopienWollte.datev && !res.datev_vorhanden,
              umbenannt: kopienWollte.umbenannt && !res.umbenannt_vorhanden,
            };
            setDuplikatInfo((prev) => (prev ? { ...prev, kopienStatus: { fertig: true, ...res } } : prev));
          })
          .catch(() => {
            // Check fehlgeschlagen → konservativ: keine Kopien, User sieht Hinweis
            setDuplikatInfo((prev) => (prev ? { ...prev, kopienStatus: { fertig: true, fehler: true } } : prev));
          });
      }
      return;
    }

    await _doMove(datei);
  }, [selected, fileSettings, manuellOrdner, wiederholenOrdner, checkDuplikat, _doMove]);
  handleMoveRef.current = handleMove;

  /* == Duplikat-Popup: fehlende Metadaten der existierenden Datei nachladen == */
  useEffect(() => {
    if (!duplikatInfo) return;
    const existPath = duplikatInfo.existierend?.path;
    if (!existPath || duplikatInfo.existierend?.size != null) return;
    // Metadaten via API holen
    api("/verschieben/file-meta?path=" + encodeURIComponent(existPath))
      .then((meta) => {
        setDuplikatInfo((prev) => {
          if (!prev) return prev;
          return { ...prev, existierend: { ...prev.existierend, size: meta.size, modified: meta.modified } };
        });
      })
      .catch(() => {});
  }, [duplikatInfo]);


  const autoReady = dateien.filter((d) => {
    if (inFlightPaths.has(d.path)) return false;
    const s = fileSettings[d.path];
    return s?.confidence === "auto" && !!s.ziel_ordner;
  });

  const handleAutoVerschieben = useCallback(async () => {
    setAutoMoving(true);
    setAutoResult(null);
    // Sofort sichtbar: Zeilen ausgrauen + Status in der Ecke
    grauEin(autoReady.map((d) => d.path));
    setJobVorlauf("Duplikat-Prüfung läuft…");
    try {
      // 1) Duplikat-Check
      const checkItems = autoReady.map((d) => {
        const s = fileSettings[d.path];
        return { quell_pfad: d.path, ziel_ordner: s.ziel_ordner, name: d.name };
      });
      const dups = await checkBatchDuplikate(checkItems);
      const dupPaths = new Set(dups.map((d) => d.quell?.quell_pfad));
      if (dups.length > 0) setDuplikatList(dups);
      grauAus([...dupPaths]); // Duplikate bleiben in der Liste → wieder aktiv

      // 2) Nur Nicht-Duplikate verschieben
      const nonDupReady = autoReady.filter((d) => !dupPaths.has(d.path));
      if (nonDupReady.length === 0) return;

      const batch = nonDupReady.map((d) => {
        const item = buildJobItem(d);
        item.ziel_ordner = fileSettings[d.path]?.ziel_ordner || "";
        item.auto_verschieben = true;
        item.automatisch = true;
        item.skip_db = false;
        return item;
      });
      await submitJob(batch);   // Hintergrund-Job: UI bleibt frei, Indikator zeigt Fortschritt
    } finally {
      setJobVorlauf("");
      setAutoMoving(false);
    }
  }, [autoReady, fileSettings, checkBatchDuplikate, buildJobItem, submitJob, grauEin, grauAus]);

  /* == Batch == */
  const batchReady = dateien.filter((d) => {
    if (inFlightPaths.has(d.path)) return false;
    const s = fileSettings[d.path];
    if (s?.confidence === "manuell") return !!manuellOrdner;
    if (s?.confidence === "wiederholen") return !!wiederholenOrdner;
    return s?.confidence === "green" && !!s.ziel_ordner;
  });

  const handleBatch = useCallback(async () => {
    setShowBatch(false);   // Popup sofort zu — alles Weitere läuft im Hintergrund
    setBatchMoving(true);
    setBatchResult(null);
    // Sofort sichtbar: Zeilen ausgrauen + Status in der Ecke
    grauEin(batchReady.map((d) => d.path));
    setJobVorlauf("Duplikat-Prüfung läuft…");
    try {
      // 1) Duplikat-Check
      const checkItems = batchReady.map((d) => {
        const s = fileSettings[d.path];
        const ziel = s.confidence === "manuell"     ? manuellOrdner
                   : s.confidence === "wiederholen" ? wiederholenOrdner
                   : s.ziel_ordner;
        return { quell_pfad: d.path, ziel_ordner: ziel, name: d.name };
      });
      const dups = await checkBatchDuplikate(checkItems);
      const dupPaths = new Set(dups.map((d) => d.quell?.quell_pfad));
      if (dups.length > 0) setDuplikatList(dups);
      grauAus([...dupPaths]); // Duplikate bleiben in der Liste → wieder aktiv

      // 2) Nur Nicht-Duplikate verschieben
      const nonDupReady = batchReady.filter((d) => !dupPaths.has(d.path));
      if (nonDupReady.length === 0) return;

      await submitJob(nonDupReady.map((d) => buildJobItem(d)));  // Hintergrund-Job
    } finally {
      setJobVorlauf("");
      setBatchMoving(false);
    }
  }, [batchReady, fileSettings, manuellOrdner, wiederholenOrdner, checkBatchDuplikate, buildJobItem, submitJob, grauEin, grauAus]);

  /* == Checked-Batch: markierte Dateien verschieben == */
  const handleCheckedBatch = useCallback(async () => {
    const items = dateien.filter((d) => checkedPaths.has(d.path) && !inFlightPaths.has(d.path));
    if (items.length === 0) return;
    setShowBatch(false);   // Popup sofort zu — Duplikat-Fragen kommen einzeln, Rest läuft im Hintergrund
    // Sofort sichtbar: Zeilen ausgrauen + Status in der Ecke
    grauEin(items.map((d) => d.path));
    setJobVorlauf("Duplikat-Prüfung läuft…");
    setBatchMoving(true);
    setBatchResult(null);

    // 1) Duplikat-Erkennung: BEIDE Quellen kombinieren
    //    a) Bereits in d.duplikat bekannte (Snapshot-basiert, erkennt cross-folder)
    //    b) Zusätzlich API-Check (erkennt Namens-Konflikte im Zielordner)
    const knownDupPaths = new Set();
    const dups = [];

    // a) Aus d.duplikat der bereits geladenen Dateien
    for (const d of items) {
      if (!d.duplikat) continue;
      const s = fileSettings[d.path] || {};
      const ziel = s.confidence === "manuell"     ? manuellOrdner
                 : s.confidence === "wiederholen" ? wiederholenOrdner
                 : s.ziel_ordner;
      dups.push({
        quell: { quell_pfad: d.path, ziel_ordner: ziel, name: d.name },
        existierend: {
          path: d.duplikat.existing_path,
          name: d.duplikat.existing_name,
          size: d.duplikat.existing_size,
          modified: d.duplikat.existing_modified,
        },
      });
      knownDupPaths.add(d.path);
    }

    // b) API-Check für Dateien die noch nicht als Duplikat bekannt sind
    const checkItems = items
      .filter((d) => !knownDupPaths.has(d.path))
      .map((d) => {
        const s = fileSettings[d.path] || {};
        const ziel = s.confidence === "manuell"     ? manuellOrdner
                   : s.confidence === "wiederholen" ? wiederholenOrdner
                   : s.ziel_ordner;
        return { quell_pfad: d.path, ziel_ordner: ziel, name: d.name };
      });
    const apiDups = await checkBatchDuplikate(checkItems);
    dups.push(...apiDups);

    const dupPaths = new Set(dups.map((d) => d.quell?.quell_pfad));

    // 2) Bei Duplikaten nachfragen: pro Duplikat einzeln fragen
    let replacedPaths = new Set();
    let skipPaths = new Set();
    const dupJobItems = [];          // Duplikat-Auflösungen, die als Job-Move laufen
    const dupJobPaths = new Set();
    if (dups.length > 0) {
      setShowBatch(false); // Batch-Popup schließen damit Duplikat-Popup sichtbar ist
      for (const dup of dups) {
        const quellPfad = dup.quell?.quell_pfad || dup.quell?.path;
        const s = fileSettings[quellPfad] || {};
        const zielOrdner = s.confidence === "manuell"     ? manuellOrdner
                         : s.confidence === "wiederholen" ? wiederholenOrdner
                         : s.ziel_ordner || checkItems.find?.((c) => c.quell_pfad === quellPfad)?.ziel_ordner;
        const altOrdner = dup.existierend?.path?.replace(/\/[^/]+$/, "");
        const skipDb = s.confidence === "manuell" || s.confidence === "wiederholen";

        // Popup zeigen und auf Auswahl warten (Promise)
        const choice = await new Promise((resolve) => {
          setDuplikatInfo({
            quell: {
              name: dup.quell?.name || quellPfad?.split("/").pop(),
              path: quellPfad,
              size: null,
              modified: null,
            },
            existierend: dup.existierend,
            zielOrdner,
            altOrdner,
            onReplace: () => { setDuplikatInfo(null); resolve("replace_old"); },
            onReplaceNew: () => { setDuplikatInfo(null); resolve("replace_new"); },
            onDelete: () => { setDuplikatInfo(null); resolve("delete_new"); },
            onKeepBoth: () => { setDuplikatInfo(null); resolve("keep_both"); },
            onAbort: () => { setDuplikatInfo(null); resolve("abort"); },
          });
        });

        if (choice === "replace_old" && altOrdner) {
          // Alte Datei löschen; der Move läuft über den Job (gleiche Pipeline
          // wie jedes Verschieben — inkl. Trello, falls angehakt)
          try {
            await apiPost("/verschieben/loeschen", { pfad: dup.existierend.path });
            dupJobItems.push(buildJobItem({ path: quellPfad }, altOrdner, { kopie_datev: false, kopie_umbenannt: false }));
            dupJobPaths.add(quellPfad);
          } catch (e) { console.warn("Duplikat-Ersetzen fehlgeschlagen:", e); }
        } else if (choice === "replace_new" && zielOrdner) {
          try {
            await apiPost("/verschieben/loeschen", { pfad: dup.existierend.path });
            dupJobItems.push(buildJobItem({ path: quellPfad }, zielOrdner));
            dupJobPaths.add(quellPfad);
          } catch (e) { console.warn("Duplikat replace_new fehlgeschlagen:", e); }
        } else if (choice === "delete_new") {
          try {
            await apiPost("/verschieben/loeschen", { pfad: quellPfad });
            replacedPaths.add(quellPfad); // aus Liste entfernen
          } catch (e) { console.warn("Duplikat delete_new fehlgeschlagen:", e); }
        } else if (choice === "keep_both") {
          // Neue in vorgeschlagenen Ordner, alte bleibt → normaler Batch
        } else {
          // abort → Datei bleibt in Liste, überspringen
          skipPaths.add(quellPfad);
        }
      }
    }

    // 3) Nicht-Duplikate + aufgelöste Duplikate verschieben
    // - replacedPaths (delete_new): Datei gelöscht → aus Batch + Liste raus
    // - dupJobPaths (replace_old/new): laufen als Job-Items mit
    // - skipPaths: vom User übersprungen → aus Batch raus
    // - keep_both Duplikate: normaler Batch übernimmt (beide behalten)
    const excluded = new Set([...replacedPaths, ...skipPaths, ...dupJobPaths]);
    const nonDupItems = items.filter((d) => !excluded.has(d.path));
    // Übersprungene/gelöschte Duplikate wieder aktiv machen
    grauAus([...skipPaths, ...replacedPaths]);
    if (nonDupItems.length === 0 && replacedPaths.size === 0 && dupJobItems.length === 0) {
      grauAus(items.map((d) => d.path));
      setJobVorlauf("");
      setBatchMoving(false); setShowBatch(false); return;
    }

    // Ersetzte + übersprungene Dateien aus Liste entfernen
    const toRemove = new Set([...replacedPaths, ...skipPaths]);
    if (toRemove.size > 0) {
      setCheckedPaths((prev) => { const n = new Set(prev); toRemove.forEach((p) => n.delete(p)); return n; });
      setDateien((prev) => {
        const next = prev.filter((d) => !replacedPaths.has(d.path));
        setSelectedIdx((i) => Math.min(i, next.length - 1));
        return next;
      });
    }

    // Restliche Dateien + aufgelöste Duplikate als EIN Hintergrund-Job
    await submitJob([...dupJobItems, ...nonDupItems.map((d) => buildJobItem(d))]);
    setJobVorlauf("");
    setBatchMoving(false);
    setShowBatch(false);
  }, [dateien, checkedPaths, fileSettings, manuellOrdner, wiederholenOrdner, checkBatchDuplikate, buildJobItem, submitJob, grauEin, grauAus]);

  /* == Counts == */
  const greenCount   = dateien.filter((d) => fileSettings[d.path]?.confidence === "green").length;
  const yellowCount  = dateien.filter((d) => fileSettings[d.path]?.confidence === "yellow").length;
  const manuellCount    = dateien.filter((d) => fileSettings[d.path]?.confidence === "manuell").length;
  const wiederholenCount = dateien.filter((d) => fileSettings[d.path]?.confidence === "wiederholen").length;
  const redCount         = dateien.filter((d) => fileSettings[d.path]?.confidence === "red").length;
  const autoCount        = dateien.filter((d) => fileSettings[d.path]?.confidence === "auto").length;

  /* == Checked-Batch: markierte Dateien mit gültigem Zielordner == */
  const checkedDateien = dateien.filter((d) => checkedPaths.has(d.path));
  const batchDisplayList = checkedPaths.size > 0 ? checkedDateien : batchReady;
  const batchIsChecked = checkedPaths.size > 0;

  /* == Quick-Folder-Dropdown: Basis-Pfad aus outbox ableiten == */
  const batchBase = useMemo(() => {
    if (!outbox) return "";
    const m = outbox.match(/^(.*##Rename-me Batch)/);
    return m ? m[1] : outbox.replace(/\/[^/]+$/, "");
  }, [outbox]);

  const QUICK_FOLDERS = [
    { label: "Output", sub: "/Batch Output"        },
    { label: "NOK",    sub: "/Batch Fehler/#NOK"   },
    { label: "Fehler", sub: "/Batch Fehler"        },
    { label: "Input",  sub: "/Batch Input"         },
  ];

  const outboxNorm = (outbox || "").replace(/\/$/, "");
  const currentQuick = QUICK_FOLDERS.find((f) => outboxNorm.endsWith(f.sub))?.sub ?? "";

  /* ======= RENDER ======= */

  if (phase === "loading" || phase === "analysing") {
    return (
      <div className="verschieben-page">
        <div className="v-loading-screen">
          <div className="v-loading-anim">
            <div className="v-pulse-ring" />
            {phase === "analysing" ? <FaSearch className="v-loading-icon" /> : <FaFolder className="v-loading-icon" />}
          </div>
          <div className="v-loading-text">{loadMsg}</div>
          <div className="v-loading-sub">
            {phase === "analysing" ? "Snapshot wird gebaut & DB-Historie abgefragt..." : "Dropbox Output-Ordner wird gelesen..."}
          </div>
        </div>
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="verschieben-page">
        <div className="v-error-screen">
          <FaTimes className="v-error-icon" />
          <h3>Fehler beim Laden</h3>
          <p>{error}</p>
          {sourceFolder && (
            <p className="v-error-hint">Quell-Ordner: <code>{sourceFolder}</code></p>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button className="v-btn primary" onClick={() => loadDateien()}>Erneut versuchen</button>
            {sourceFolder && (
              <button className="v-btn ghost" onClick={resetSrcFolder}>Standard-Ordner verwenden</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="verschieben-page">
      {/* Lade-Overlay bei aktiven Aktionen */}
      {(!!reprocessingPath || !!analysingPath) && (
        <div className="v-action-overlay">
          <div className="v-action-spinner">
            <FaSync className="spin" />
            <span>
              {autoMoving ? "Auto-Verschieben läuft…"
                : batchMoving ? "Batch-Verschieben läuft…"
                : moving ? "Verschieben…"
                : reprocessingPath ? "Pipeline läuft…"
                : "Analyse läuft…"}
            </span>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="v-header">
        <button className="v-btn ghost" onClick={() => navigate("/")}>&#8592; Home</button>
        <h2 className="v-title">Verschieben</h2>
        {analysed && (
          <div className="v-stats">
            {autoCount > 0 && <span className="v-tag" style={{ background: "#0d2e2e", border: "1px solid #00bcd4", color: "#4dd0e1" }}>⚡ {autoCount}</span>}
            <span className="v-tag green">{greenCount}</span>
            <span className="v-tag yellow">{yellowCount}</span>
            <span className="v-tag red">{redCount}</span>
          </div>
        )}
        <span className="v-tag total">{dateien.length} Dateien</span>
        {analysed && <span className="v-tag snap" title="Gecachte Ordner">{snapshotCount} Ordner</span>}
        {analysed && berechnetAm && !scanLaeuft && (
          <span
            className={"v-tag " + (vonCache ? "cache" : "fresh")}
            title={vonCache ? "Aus DB-Cache (täglich aktualisiert)" : "Frisch von Dropbox gescannt"}
          >
            {vonCache ? "🗃 Cache" : "✓ Frisch"}: {new Date(berechnetAm).toLocaleString("de-DE", {day:"2-digit",month:"2-digit",hour:"2-digit",minute:"2-digit"})}
          </span>
        )}
        {scanLaeuft && (
          <span className="v-tag scan-running" title="Dropbox wird gerade gescannt, Vorschläge werden in Kürze aktualisiert">
            🔄 Scan läuft...
          </span>
        )}
        <div className="v-header-actions">
          {analysing ? (
            <button className="v-btn analyse disabled" disabled title="Analyse läuft im Hintergrund — Liste bleibt bedienbar">
              <FaSync className="spin" /> wird analysiert…
            </button>
          ) : !analysed ? (
            <button className="v-btn analyse" onClick={runAnalyse} title="Vorschläge aus Cache laden">
              <FaSearch /> Analyse starten
            </button>
          ) : (
            <button
              className={"v-btn ghost small" + (scanLaeuft ? " disabled" : "")}
              onClick={scanLaeuft ? undefined : runFreshAnalyse}
              title={scanLaeuft ? "Scan läuft bereits im Hintergrund..." : "Dropbox neu scannen → Neuer Cache"}
            >
              <FaSync /> Neuer Cache
            </button>
          )}
          <button className="v-btn ghost" onClick={() => loadDateien()} disabled={analysing} title="Neu laden — Analyse startet automatisch im Hintergrund"><FaSync /> Neu Laden</button>
          {checkedPaths.size > 0 && (
            <div className="v-header-marked">
              <button className="v-btn primary" onClick={() => setShowBatch(true)} title="Markierte Dateien verschieben">
                <FaCheck /> Verschieben ({checkedPaths.size})
              </button>
              <button className="v-btn" style={{ background: "#5c3d00", color: "#ffb74d", border: "1px solid #ff9800" }} onClick={async () => {
                if (!window.confirm(`${checkedPaths.size} markierte Datei(en) in NOK verschieben?`)) return;
                for (const p of checkedPaths) {
                  try { await apiPost("/verschieben/loeschen", { pfad: p, nok: true }); } catch {}
                }
                setDateien((prev) => {
                  const next = prev.filter((d) => !checkedPaths.has(d.path));
                  setSelectedIdx((i) => Math.min(i, next.length - 1));
                  return next;
                });
                setCheckedPaths(new Set());
              }} title="Markierte Dateien in NOK-Ordner verschieben">
                NOK verschieben ({checkedPaths.size})
              </button>
              <button className="v-btn danger" onClick={async () => {
                if (!window.confirm(`${checkedPaths.size} markierte Datei(en) wirklich löschen?`)) return;
                for (const p of checkedPaths) {
                  try { await apiPost("/verschieben/loeschen", { pfad: p }); } catch {}
                }
                setDateien((prev) => {
                  const next = prev.filter((d) => !checkedPaths.has(d.path));
                  setSelectedIdx((i) => Math.min(i, next.length - 1));
                  return next;
                });
                setCheckedPaths(new Set());
              }} title="Markierte Dateien löschen">
                <FaTrash /> Löschen ({checkedPaths.size})
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Source Folder Bar */}
      <div className="v-source-bar">
        <FaFolder className="v-source-icon" />
        <span className="v-source-label">Quell-Ordner:</span>
        <span className="v-source-path" title={outbox}>{shortPath(outbox) || outbox}</span>
        {batchBase && (
          <select
            className="v-quick-select"
            value={currentQuick}
            title="Schnell zwischen Batch-Ordnern wechseln"
            onChange={(e) => { if (e.target.value) pickSrcFolder(batchBase + e.target.value); }}
          >
            <option value="">— Ordner —</option>
            {QUICK_FOLDERS.map((f) => (
              <option key={f.sub} value={f.sub}>{f.label}</option>
            ))}
          </select>
        )}
        <button className="v-btn ghost small" onClick={openSrcBrowse} title="Anderen Quell-Ordner wählen">
          <FaEdit /> Ändern
        </button>
      </div>

      {/* Source Folder Browse Modal */}
      {srcBrowseOpen && (
        <div className="v-overlay" onClick={() => setSrcBrowseOpen(false)}>
          <div className="v-modal" onClick={(e) => e.stopPropagation()}>
            <div className="v-modal-header">
              <h3><FaFolderOpen /> Quell-Ordner wählen</h3>
              <button className="v-dismiss" onClick={() => setSrcBrowseOpen(false)}>&#215;</button>
            </div>
            <p className="v-modal-desc">Wähle den Ordner, aus dem Dateien geladen werden sollen:</p>
            <div className="v-browse-crumbs" style={{ padding: "0.4rem 0", marginBottom: "0.5rem" }}>
              {srcBrowsePath.split("/").filter(Boolean).reduce((acc, part) => {
                const p = (acc.length > 0 ? acc[acc.length - 1].path : "") + "/" + part;
                acc.push({ name: part, path: p });
                return acc;
              }, []).map((seg, i) => (
                <span key={seg.path}>
                  {i > 0 && <span className="v-crumb-sep">/</span>}
                  <button className="v-crumb" onClick={() => browseSrcFolder(seg.path)}>{seg.name}</button>
                </span>
              ))}
            </div>
            {srcBrowseLoading ? (
              <div className="v-browse-loading">Lade...</div>
            ) : (
              <div className="v-browse-items" style={{ maxHeight: "400px", overflowY: "auto", border: "1px solid #333", borderRadius: "6px" }}>
                <button className="v-browse-item pick" onClick={() => pickSrcFolder(srcBrowsePath)}>
                  <FaCheck /> Diesen Ordner verwenden
                </button>
                {srcNewFolderMode ? (
                  <div className="v-new-folder-row">
                    <FaFolderPlus className="v-new-folder-icon" />
                    <input
                      className="v-new-folder-input"
                      autoFocus
                      placeholder="Ordner-Name..."
                      value={srcNewFolderName}
                      onChange={(e) => setSrcNewFolderName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { createFolder(srcBrowsePath, srcNewFolderName, browseSrcFolder); setSrcNewFolderMode(false); setSrcNewFolderName(""); }
                        if (e.key === "Escape") { setSrcNewFolderMode(false); setSrcNewFolderName(""); }
                      }}
                    />
                    <button className="v-btn ghost small" title="Erstellen" onClick={() => { createFolder(srcBrowsePath, srcNewFolderName, browseSrcFolder); setSrcNewFolderMode(false); setSrcNewFolderName(""); }}><FaCheck /></button>
                    <button className="v-btn ghost small" title="Abbrechen" onClick={() => { setSrcNewFolderMode(false); setSrcNewFolderName(""); }}><FaTimes /></button>
                  </div>
                ) : (
                  <button className="v-browse-item new-folder" onClick={() => { setSrcNewFolderMode(true); setSrcNewFolderName(""); }}>
                    <FaFolderPlus /> Neuen Ordner erstellen
                  </button>
                )}
                {srcBrowseItems.map((item) => (
                  <button key={item.path} className="v-browse-item"
                    onClick={() => browseSrcFolder(item.path)}>
                    <FaFolder /> {item.name}
                  </button>
                ))}
                {srcBrowseItems.length === 0 && <div className="v-browse-empty">Keine Unterordner</div>}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="v-keyboard-hint">
        <FaArrowUp /> <FaArrowDown /> navigieren
      </div>

      {/* Split View */}
      <div className="v-split">
        {/* LEFT: File List with collapsible groups */}
        <div className="v-left-col">
          {/* Filter Bar */}
          <div className="v-filter-bar">
            <FaSearch className="v-filter-icon" />
            <input
              className="v-filter-input"
              style={{ maxWidth: 110 }}
              placeholder="Prefix..."
              list="v-prefix-options"
              value={filterPrefix}
              onChange={(e) => setFilterPrefix(e.target.value)}
            />
            <datalist id="v-prefix-options">
              {prefixOptions.map((p) => <option key={p} value={p} />)}
            </datalist>
            <input
              className="v-filter-input"
              style={{ maxWidth: 100 }}
              type="date"
              placeholder="Datum"
              value={filterDatum}
              onChange={(e) => setFilterDatum(e.target.value)}
            />
            <input
              className="v-filter-input"
              placeholder="Freitext (Leerzeichen = UND)..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
            {(filterText || filterPrefix || filterDatum) && (
              <button className="v-btn ghost small" onClick={() => { setFilterText(""); setFilterPrefix(""); setFilterDatum(""); }} title="Filter löschen">
                <FaTimes />
              </button>
            )}
            <button
              className="v-btn ghost small"
              title={groupMode === "ampel" ? "Sortierung: nach Ampel (Sicher/Möglich) — klicken für Präfix" : "Sortierung: nach Präfix — klicken für Ampel"}
              onClick={() => setGroupMode((m) => (m === "ampel" ? "prefix" : "ampel"))}
              style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
            >
              {groupMode === "ampel" ? <><FaLayerGroup /> Ampel</> : <><FaTag /> Präfix</>}
            </button>
            <span className="v-filter-count">{filteredDateien.length}/{dateien.length}</span>
          </div>
          <div className="v-file-list" ref={listRef}>
          {filteredDateien.length === 0 ? (
            <div className="v-empty">
              {dateien.length === 0
                ? "Keine Dateien im Output-Ordner."
                : `Keine Ergebnisse für „${filterText}"`}
            </div>
          ) : (() => {
            /* Dateien gruppieren — nach Ampel (Confidence) oder nach Präfix */
            const grouped = {};
            filteredDateien.forEach((d) => {
              const origIdx = dateien.indexOf(d);
              const key = groupMode === "prefix"
                ? ((d.parsed?.prefix || "?").trim() || "?")
                : (d.path in lockedGroups ? lockedGroups[d.path] : ((fileSettings[d.path] || {}).confidence || "none"));
              if (!grouped[key]) grouped[key] = [];
              grouped[key].push({ d, origIdx });
            });

            // Reihenfolge: Ampel = feste GROUP_ORDER, Präfix = alphabetisch
            const groupList = groupMode === "prefix"
              ? Object.keys(grouped).sort((a, b) => a.localeCompare(b, "de"))
                  .map((k) => ({ key: k, label: k, icon: "■" }))
              : GROUP_ORDER;

            const toggleGroup = (key) => {
              setCollapsedGroups(prev => {
                const next = new Set(prev);
                if (next.has(key)) next.delete(key); else next.add(key);
                return next;
              });
            };

            return groupList.filter(g => grouped[g.key]?.length > 0).map(g => {
              const items = grouped[g.key];
              const gc = CONF[g.key] || CONF.none;
              const isCollapsed = collapsedGroups.has(g.key);
              const groupPaths = items.map(({ d }) => d.path);
              const allChecked = groupPaths.every(p => checkedPaths.has(p));
              const someChecked = !allChecked && groupPaths.some(p => checkedPaths.has(p));
              const toggleGroupCheck = (e) => {
                e.stopPropagation();
                setCheckedPaths(prev => {
                  const n = new Set(prev);
                  if (allChecked) groupPaths.forEach(p => n.delete(p));
                  else groupPaths.forEach(p => n.add(p));
                  return n;
                });
              };
              return (
                <div key={g.key} className="v-group">
                  <button
                    className="v-group-header"
                    style={{ borderLeftColor: gc.border, color: gc.text }}
                    onClick={() => toggleGroup(g.key)}
                  >
                    <span className="v-group-icon">{g.icon}</span>
                    <span className="v-group-label">{g.label}</span>
                    <span className="v-group-count">{items.length}</span>
                    <input
                      type="checkbox"
                      className="v-group-check"
                      checked={allChecked}
                      ref={el => { if (el) el.indeterminate = someChecked; }}
                      title={allChecked ? "Alle abwählen" : "Alle auswählen"}
                      onClick={toggleGroupCheck}
                      onChange={() => {}}
                    />
                    <span className={"v-group-chevron" + (isCollapsed ? " collapsed" : "")}>&#9660;</span>
                  </button>
                  {!isCollapsed && items.map(({ d, origIdx: idx }) => {
                    const s = fileSettings[d.path] || {};
                    // lockedGroups für Gruppierung, fileSettings für angezeigte Farbe
                    const groupConf = d.path in lockedGroups ? lockedGroups[d.path] : (s.confidence || "none");
                    const conf = s.confidence || groupConf;
                    const c = CONF[conf] || CONF.none;
                    const isSel = idx === selectedIdx;
                    const inFlight = inFlightPaths.has(d.path);
                    return (
                      <div
                        key={d.path}
                        ref={(el) => (cardRefs.current[idx] = el)}
                        {...kachelDragProps(d.path, d.name)}
                        className={"v-card" + (isSel ? " active" : "")}
                        style={{ borderLeftColor: c.border, ...(inFlight ? { opacity: 0.45, pointerEvents: "none" } : {}) }}
                        title={inFlight ? "Wird gerade verschoben…" : undefined}
                        onClickCapture={() => { setSelectedIdx(idx); setMoveResult(null); }}
                      >
                        <div className="v-card-row1">
                          <span className="v-prefix" title={d.parsed?.prefix}>{d.parsed?.prefix || "?"}</span>
                          {d.nok && (
                            <span
                              className="v-nok-flag"
                              title="Rename-me konnte Prefix oder Absender nicht erkennen — Namen prüfen/korrigieren"
                            >⚠ Prüfen</span>
                          )}
                          {/* Bedingte Buttons LINKS */}
                          <div className="v-card-special-btns">
                            {d.name.includes("#") && (
                              <button
                                className="v-special-btn hash"
                                title="# aus Dateiname entfernen"
                                onClick={(e) => { e.stopPropagation(); handleRename(d.path, d.name.replaceAll("#", "")); }}
                              >#</button>
                            )}
                            {/ - bwa - /i.test(d.name) && / - gesamt\.pdf$/i.test(d.name) && (
                              <label className="v-mini-check bwa" title="BWA UStVA-Seite extrahieren vor dem Verschieben">
                                <input type="checkbox" checked={s.bwa_split || false}
                                  onChange={(e) => { e.stopPropagation(); toggleSetting(d.path, "bwa_split"); }} />
                                <span style={{ color: s.bwa_split ? "#fbbf24" : undefined }}>BWA</span>
                              </label>
                            )}
                          </div>
                          {editingPath === d.path ? (
                            <input
                              className="v-fname-edit"
                              autoFocus
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") { e.stopPropagation(); handleRename(d.path, editingName); }
                                if (e.key === "Escape") { e.stopPropagation(); setEditingPath(null); }
                              }}
                              onBlur={() => handleRename(d.path, editingName)}
                            />
                          ) : (
                            <span
                              {...contextShareProps(d.path, d.name)}
                              className="v-fname"
                              title={"Klicken zum Umbenennen — Kachel ziehen = PDF ablegen, Rechtsklick = Teilen: " + d.name}
                              onClick={(e) => { e.stopPropagation(); setEditingPath(d.path); setEditingName(d.name); }}
                            >{d.name}</span>
                          )}
                          <button className="v-copy-btn" title="Dateiname kopieren" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(d.name); }}><FaCopy /></button>
                          <button
                            className="v-copy-btn"
                            title="Klicken = Teilen · die ganze Kachel ziehen = PDF in Mail, Teams, Finder/Explorer … ablegen"
                            onClick={(e) => { e.stopPropagation(); shareFile(d.path, d.name); }}
                          ><FaShareAlt /></button>
                          <div className="v-card-checks">
                            <label className="v-mini-check" title="Kopie Datev">
                              <input type="checkbox" checked={s.kopie_datev || false}
                                onChange={(e) => { e.stopPropagation(); toggleSetting(d.path, "kopie_datev"); }} />
                              <span>Datev</span>
                            </label>
                            <label className="v-mini-check" title="Kopie Umbenannt">
                              <input type="checkbox" checked={s.kopie_umbenannt || false}
                                onChange={(e) => { e.stopPropagation(); toggleSetting(d.path, "kopie_umbenannt"); }} />
                              <span>Umb.</span>
                            </label>
                            <label className="v-mini-check" title="Duplikat erstellen (jetzt ohne Verschieben oder nach dem Verschieben)"
                              style={{ cursor: "pointer" }}
                              onClick={(e) => { e.stopPropagation(); setDuplikatPickerFile(d); }}>
                              <FaClone style={{ color: s.duplikat_quelle ? "#da70d6" : undefined }} />
                              <span style={{ color: s.duplikat_quelle ? "#da70d6" : undefined }}>Dupl.</span>
                            </label>
                            <label className="v-mini-check nok" title={s.nok_original ? "NOK — Original direkt in Fehler-Ordner" : "NOK — Kopie in Fehler-Ordner"}>
                              <input type="checkbox" checked={s.kopie_nok || false}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  if (s.kopie_nok || s.nok_original) {
                                    setFileSettings(prev => ({ ...prev, [d.path]: { ...prev[d.path], kopie_nok: false, nok_original: false } }));
                                  } else {
                                    setNokPickerFile(d);
                                  }
                                }} />
                              <span style={{ color: s.nok_original ? "#f97316" : s.kopie_nok ? "#ef4444" : undefined }}>{s.nok_original ? "NOK!" : "NOK"}</span>
                            </label>
                            <label className="v-mini-check v-trello-check" title="Trello-Card erstellen">
                              <input type="checkbox" checked={s.trello_enabled || false}
                                onChange={(e) => { e.stopPropagation(); if (!s.trello_enabled) { openTrelloPopup(d.path); } else { setFileSettings(prev => ({ ...prev, [d.path]: { ...prev[d.path], trello_enabled: false } })); } }} />
                              <span style={{ color: s.trello_enabled ? "#9c27b0" : undefined }}>Trello</span>
                            </label>
                          </div>
                        </div>
                        {/* Welle 79: Alternativer Name der anderen Pipeline */}
                        {((d.alternativen || []).length > 0 || (d.hat_gemini_vorschlag === false && !d.nok)) && !inFlight && (
                          <div className="v-card-alt" onClick={(e) => e.stopPropagation()}>
                            {(d.alternativen || []).map((a) => (
                              <button
                                key={a.quelle}
                                className={`v-alt-chip v-alt-${a.quelle}`}
                                disabled={reprocessingPath === d.path}
                                title={`${a.quelle === "gemini" ? "Gemini" : "Pipeline 1"} schlägt vor: ${a.name} — klicken = übernehmen`}
                                onClick={() => uebernehmeNamen(d, a.name, a.quelle === "gemini" ? "gemini_batch" : "v1_batch", a.session_id || null, a.name)}
                              >
                                {a.quelle === "gemini" ? "✨ Gemini" : "v1"} → <span className="v-alt-name">{a.name}</span> ✅
                              </button>
                            ))}
                            {d.hat_gemini_vorschlag === false && (
                              <button
                                className="v-alt-chip v-alt-gemini v-alt-run"
                                title="Für diese Datei liegt noch kein Gemini-Vorschlag vor — jetzt rechnen"
                                onClick={() => startKi2(d, "gemini")}
                              >✨ Gemini rechnen</button>
                            )}
                            {/* Welle 86: Platzhalter im Namen → Gemini erneut (der Batch macht das automatisch nach) */}
                            {d.hat_gemini_vorschlag !== false && hatPlatzhalter(d.name) && (
                              <button
                                className="v-alt-chip v-alt-gemini v-alt-run"
                                title="Name enthält Platzhalter (###, XXX, ####-##-##, #-Segment) — Gemini erneut rechnen. Im Batch läuft das bei Platzhaltern automatisch nach."
                                onClick={() => startKi2(d, "gemini")}
                              >✨ Gemini erneut</button>
                            )}
                          </div>
                        )}
                        <div className="v-card-row2">
                          <span className="v-meta">{d.parsed?.absender || ""}</span>
                          <span className="v-meta dim">{d.parsed?.datum || ""}</span>
                          {d.parsed?.rechnungsnummer && <span className="v-meta dim">#{d.parsed.rechnungsnummer}</span>}
                          <div className="v-conf-icons">
                            {[
                              { val: "green",      icon: "●", title: "Sicher" },
                              { val: "yellow",     icon: "◐", title: "Möglich" },
                              { val: "red",        icon: "○", title: "Unbekannt" },
                              { val: "manuell",    icon: "★", title: "Manuell" },
                              { val: "wiederholen",icon: "↺", title: "Wiederholen" },
                              { val: "todo",       icon: "⏳", title: "Wiedervorlage — bleibt liegen" },
                            ].map(({ val, icon, title }) => (
                              <button
                                key={val}
                                className={"v-conf-icon-btn" + (conf === val ? " active" : "")}
                                style={conf === val ? { color: CONF[val].text, borderColor: CONF[val].border } : {}}
                                title={title}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const extra = val === "wiederholen" ? { ziel_ordner: wiederholenOrdner }
                                               : val === "manuell"    ? { ziel_ordner: manuellOrdner }
                                               : {};
                                  setFileSettings((prev) => ({ ...prev, [d.path]: { ...prev[d.path], confidence: val, ...extra } }));
                                  saveStatusOverride(d.path, val);
                                  frozenPath.current = d.path;
                                }}
                              >{icon}</button>
                            ))}
                          </div>
                          {conf === "auto" ? (
                            <span
                              className="v-conf-select"
                              style={{ color: CONF.auto.text, borderColor: CONF.auto.border, display: "inline-flex", alignItems: "center", gap: 4, cursor: "default", fontSize: "0.8rem" }}
                              title="Automatisch erkannt – kann nicht manuell gesetzt werden"
                            >
                              ⚡ Auto
                            </span>
                          ) : (
                            <select
                              className="v-conf-select"
                              style={{ color: c.text, borderColor: c.border }}
                              value={conf}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                e.stopPropagation();
                                const val = e.target.value;
                                setFileSettings((prev) => {
                                  const extra = val === "wiederholen" ? { ziel_ordner: wiederholenOrdner }
                                               : val === "manuell"    ? { ziel_ordner: manuellOrdner }
                                               : {};
                                  return { ...prev, [d.path]: { ...prev[d.path], confidence: val, ...extra } };
                                });
                                saveStatusOverride(d.path, val);   // Welle 85: wie die Status-Icons
                                frozenPath.current = d.path;      // Sektion wechselt erst beim Zeilenwechsel
                              }}
                            >
                              <option value="none">– kein Status –</option>
                              {conf === "duplikat" && <option value="duplikat">⊘ Duplikat</option>}
                              <option value="yellow">◐ Möglich</option>
                              <option value="green">● Sicher</option>
                              <option value="manuell">★ Manuell</option>
                              <option value="wiederholen">↺ Wiederholen</option>
                              <option value="todo">⏳ Wiedervorlage</option>
                            </select>
                          )}
                        </div>
                        <div className="v-card-row3">
                          <FaArrowRight className="v-arrow-icon" style={{ color: c.text }} />
                          <div className="v-input-copy-wrap">
                            <ZielInput
                              value={s.ziel_ordner ? shortPath(s.ziel_ordner) : ""}
                              placeholder="Ziel-Ordner..."
                              title={s.ziel_ordner || ""}
                              onClick={(e) => e.stopPropagation()}
                              onCommit={(txt) => {
                                // Root-Duplikate beim Einfügen entfernen (kopierte
                                // Pfade enthalten evtl. schon /Holthey Immobilien/)
                                const clean = txt.replace(/^\/+/, "").replace(/^(?:Holthey Immobilien\/)+/i, "");
                                updateZielTyping(d.path, "/Holthey Immobilien/" + clean);
                              }}
                            />
                            <button className="v-input-copy-btn" title="Pfad kopieren" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(shortPath(s.ziel_ordner || d.path)); }}><FaCopy /></button>
                          </div>
                          <button className="v-browse-btn" title="Ordner durchsuchen"
                            onClick={(e) => { e.stopPropagation(); setSelectedIdx(idx); openBrowse(d); }}>
                            <FaFolderOpen />
                          </button>
                          <button
                            className={"v-reprocess-btn" + (reprocessingPath === d.path ? " loading" : "")}
                            tabIndex={-1}
                            title="Neu analysieren (OCR-Pipeline wählen)"
                            disabled={!!reprocessingPath}
                            onClick={(e) => { e.stopPropagation(); setPipelinePickerFile(d); }}
                          >
                            <FaSync className={reprocessingPath === d.path ? "spin" : ""} />
                          </button>
                          <div className="v-move-group">
                            <button
                              className={"v-move-btn" + (!s.ziel_ordner ? " disabled" : "") + (duplikatChecking ? " checking" : "")}
                              title={duplikatChecking ? "Prüfe auf Duplikate..." : "Verschieben"}
                              disabled={moving || duplikatChecking || !s.ziel_ordner}
                              onClick={(e) => { e.stopPropagation(); setSelectedIdx(idx); handleMove(d); }}
                            >
                              {duplikatChecking ? <FaSync className="spin" /> : <FaArrowRight />}
                            </button>
                            <input
                              type="checkbox"
                              className="v-check-batch"
                              title="Für Massenverarbeitung markieren"
                              checked={checkedPaths.has(d.path)}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => setCheckedPaths((prev) => {
                                const n = new Set(prev);
                                if (n.has(d.path)) n.delete(d.path); else n.add(d.path);
                                return n;
                              })}
                            />
                          </div>
                          {confirmDeletePath === d.path ? (
                            <>
                              <button className="v-delete-btn confirm" title="Ja, löschen"
                                onClick={(e) => { e.stopPropagation(); handleDelete(d.path); }}>
                                <FaCheck />
                              </button>
                              <button className="v-delete-btn cancel" title="Abbrechen"
                                onClick={(e) => { e.stopPropagation(); setConfirmDeletePath(null); }}>
                                <FaTimes />
                              </button>
                            </>
                          ) : (
                            <button className="v-delete-btn" title="Datei löschen"
                              onClick={(e) => { e.stopPropagation(); setSelectedIdx(idx); setConfirmDeletePath(d.path); }}>
                              <FaTrash />
                            </button>
                          )}
                          <div className="v-row3-separator" />
                          <button
                            className={"v-specific-btn clr-orange" + (analysingPath === d.path ? " loading" : "")}
                            disabled={!!analysingPath}
                            title="Ziel-Ordner für diese Datei neu suchen"
                            onClick={(e) => { e.stopPropagation(); lockSection(d.path, fileSettings[d.path]?.confidence); analyseEinzel(d); }}
                          >
                            <FaSearch className={analysingPath === d.path ? "spin" : ""} />
                          </button>
                          <button
                            className="v-specific-btn clr-purple"
                            title="DB-Eintrag hinzufügen (Absender, Vertrag, ...)"
                            onClick={(e) => { e.stopPropagation(); const t = SCHEMA_TO_TABLE[d.parsed?.schema];
                              if (STAMM_KAT[t]) openStammdaten(d, STAMM_KAT[t]); else openStrgPopup(d.path, t || undefined); }}
                          >
                            <FaPlus />
                          </button>
                        </div>
                        {s.reason && (
                          <div className="v-card-reason">
                            <span className={"v-src-badge " + (s.quelle === "ml" ? "v-src-badge--ml" : s.quelle === "vertrag" ? "v-src-badge--vertrag" : "v-src-badge--regel")}>
                              {s.quelle === "ml" ? "ML" : s.quelle === "vertrag" ? "Vertrag" : "Regel"}
                            </span>
                            {s.reason}
                          </div>
                        )}
                        {s.duplikat && (
                          <div style={{ color: "#da70d6", fontSize: 11, padding: "2px 8px 4px", opacity: 0.85, display: "flex", alignItems: "center", gap: 6 }}>
                            <span className="v-duplikat-card-path" title={s.duplikat.existing_path || s.duplikat.existing_name}>⊘ {s.duplikat.existing_path || s.duplikat.existing_name}</span>
                            <button
                              className="v-btn ghost"
                              style={{ fontSize: 10, padding: "1px 6px", minWidth: 0, flexShrink: 0 }}
                              title="Beide Dateien vergleichen"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedIdx(idx);
                                handleMove(d);
                              }}
                            >
                              <FaSearch style={{ marginRight: 3 }} />Anschauen
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            });
          })()}
        </div>

        {/* STRG DB-Eintrag Popup — nur über linker Spalte */}
        {sdDialog && (
          <StammdatenAnlegenDialog dateiname={sdDialog.datei?.name} vorbelegung={sdDialog.vorbelegung} startKat={sdDialog.startKat}
            onClose={() => setSdDialog(null)}
            onGespeichert={({ neuAnalysieren }) => {
              if (neuAnalysieren && sdDialog.datei?.path) { const d = sdDialog.datei; setSdDialog(null); reprocessFile(d); }
            }} />
        )}
        {strgOpen && (
          <div className="v-left-overlay" onClick={() => setStrgOpen(false)}>
            <div className="v-modal v-strg-modal" onClick={(e) => e.stopPropagation()}>
              <div className="v-modal-header">
                <h3><FaPlus /> DB-Eintrag hinzufügen</h3>
                <button className="v-dismiss" onClick={() => setStrgOpen(false)}>×</button>
              </div>
              <div className="v-strg-body">
                <label className="v-strg-field">
                  <span>Tabelle</span>
                  <select value={strgTable} onChange={(e) => {
                    setStrgTable(e.target.value);
                    setStrgForm({});
                    setStrgEditId(null); setStrgEditOriginal(null); setStrgSearch("");
                    setStrgError(""); setStrgSuccess("");
                  }}>
                    {STRG_TABLES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </label>
                {/* Welle 131: Verträge/Versicherungen/Banken/Steuern/Darlehen werden in DB 2 gepflegt */}
                <button type="button" className="v-btn" style={{ marginBottom: 8 }}
                  onClick={() => { const datei = (dateien || []).find(x => x.path === strgFilePath) || { path: strgFilePath, name: (strgFilePath || "").split("/").pop() };
                    setStrgOpen(false); openStammdaten(datei, "vertraege"); }}>
                  Vertrag / Versicherung / Bank / Steuernummer / Darlehen → Stammdaten (DB 2)
                </button>

                {/* Welle 20: Modus-Umschalter Neu / Bearbeiten */}
                <div className="v-strg-mode-toggle">
                  <button type="button"
                    className={"v-btn" + (strgMode === "new" ? " primary" : "")}
                    onClick={() => {
                      setStrgMode("new");
                      setStrgForm(strgDefaultsFor(strgRealTable));
                      setStrgEditId(null); setStrgEditOriginal(null);
                      setStrgError(""); setStrgSuccess("");
                    }}>
                    <FaPlus /> Neu
                  </button>
                  <button type="button"
                    className={"v-btn" + (strgMode === "edit" ? " primary" : "")}
                    onClick={() => {
                      setStrgMode("edit");
                      setStrgForm({});
                      setStrgEditId(null); setStrgEditOriginal(null);
                      setStrgError(""); setStrgSuccess("");
                    }}>
                    <FaEdit /> Bearbeiten
                  </button>
                </div>

                {/* Edit-Modus: Suche über alle Felder + Trefferliste */}
                {strgMode === "edit" && (
                  <>
                    <label className="v-strg-field">
                      <span>Suche (alle Felder)</span>
                      <input value={strgSearch} placeholder="z.B. Objekt, Vertragsnummer, Lieferant…"
                        onChange={(e) => setStrgSearch(e.target.value)} />
                    </label>
                    <div className="v-strg-result-list">
                      {strgRowsLoading && <div className="v-strg-result-empty">Lade…</div>}
                      {!strgRowsLoading && strgFilteredRows.length === 0 && (
                        <div className="v-strg-result-empty">Keine Treffer.</div>
                      )}
                      {!strgRowsLoading && strgFilteredRows.map(row => (
                        <div key={row.id}
                          className={"v-strg-result-row" + (strgEditId === row.id ? " selected" : "")}
                          onClick={() => strgSelectEditRow(row)}>
                          <span className="v-strg-result-id">#{row.id}</span>
                          <span className="v-strg-result-label">{strgRowLabel(row)}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Dynamische Felder aus der echten Tabellen-Struktur */}
                {(strgMode === "new" || strgEditId !== null) && (
                  (strgColumns[strgRealTable] || []).length === 0
                    ? <div className="v-strg-result-empty">Lade Spalten…</div>
                    : strgVisibleColumns(strgRealTable).map(col => strgDynField(col))
                )}

                {strgError && <div className="v-strg-error">{strgError}</div>}
                {strgSuccess && <div className="v-strg-success">{strgSuccess}</div>}
              </div>
              <div className="v-modal-actions">
                <button className="v-btn primary"
                  disabled={strgSaving || (strgMode === "edit" && strgEditId === null)}
                  onClick={() => strgSave(false)}>
                  {strgSaving ? "Speichern..." : (strgMode === "edit" ? "Änderungen speichern" : "Speichern")}
                </button>
                {strgMode === "new" && (
                  <button className="v-btn" disabled={strgSaving} onClick={() => strgSave(true)}
                    title="Speichern und anschließend Datei nochmal durch OCR-Pipeline">
                    {strgSaving ? "..." : "Speichern + Neu analysieren"}
                  </button>
                )}
                <button className="v-btn ghost" onClick={() => setStrgOpen(false)}>Abbrechen</button>
              </div>
            </div>
          </div>
        )}
        </div>

        {/* RIGHT: PDF Viewer */}
        <div className="v-preview">
          {!selected ? (
            <div className="v-preview-empty">
              <FaFolder className="v-preview-empty-icon" />
              <p>Datei ausw&auml;hlen</p>
            </div>
          ) : (
            <>
              {moveResult && (
                <div className={"v-toast " + (moveResult.ok ? "success" : "error")}>
                  {moveResult.ok ? <FaCheck /> : <FaTimes />}
                  <span>{moveResult.ok ? ("→ " + moveResult.msg) : moveResult.msg}</span>
                </div>
              )}
              {browseOpen && (
                <div className="v-browse-panel" style={{ transform: `translate(${browseDrag.x}px, ${browseDrag.y}px)` }}>
                  <div className="v-browse-header" onMouseDown={onBrowseDragStart} style={{ cursor: "move" }} title="Zum Verschieben ziehen">
                    <span className="v-browse-title">Ordner w&auml;hlen</span>
                    <button className="v-dismiss" title="Abbrechen — ursprünglicher Zielordner bleibt"
                            onClick={browseAbbrechen}>&#215;</button>
                  </div>
                  <div className="v-browse-current-path">
                    <FaFolder className="v-browse-current-icon" />
                    {browsePath ? browsePath.split("/").filter(Boolean).reduce((acc, part) => {
                      const p = (acc.length > 0 ? acc[acc.length - 1].p : "") + "/" + part;
                      acc.push({ name: part, p });
                      return acc;
                    }, []).map((seg, i, arr) => (
                      <span key={seg.p} style={{ display: "inline-flex", alignItems: "center" }}>
                        {i > 0 && <span style={{ color: "#444", margin: "0 2px" }}>/</span>}
                        <button
                          className={"v-crumb" + (i === arr.length - 1 ? " active" : "")}
                          onClick={() => browseFolder(seg.p)}
                        >{seg.name}</button>
                      </span>
                    )) : <span style={{ color: "#555" }}>…</span>}
                  </div>
                  <div className="v-browse-search" style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px" }}>
                    <FaSearch style={{ opacity: 0.6 }} />
                    <input
                      className="v-filter-input"
                      style={{ flex: 1 }}
                      placeholder="Ordner in Dropbox suchen…"
                      value={ordnerSucheQuery}
                      onChange={(e) => setOrdnerSucheQuery(e.target.value)}
                    />
                    {ordnerSucheQuery && (
                      <button className="v-btn ghost small" title="Suche leeren"
                        onClick={() => { setOrdnerSucheQuery(""); setOrdnerSucheTreffer([]); }}><FaTimes /></button>
                    )}
                  </div>
                  {ordnerSucheQuery.trim().length >= 2 && (
                    <div className="v-browse-items" style={{ maxHeight: 220, overflowY: "auto" }}>
                      {ordnerSucheTreffer.length === 0 ? (
                        <div className="v-browse-empty">Keine passenden Ordner</div>
                      ) : ordnerSucheTreffer.map((t) => (
                        <button key={t.path} className="v-browse-item" title={t.path}
                          onClick={() => { setOrdnerSucheQuery(""); setOrdnerSucheTreffer([]); browseFolder(t.path); }}>
                          <FaFolder /> {t.name}
                          <span style={{ marginLeft: "auto", fontSize: "0.68rem", color: "#666", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 160 }}>{shortPath(t.path)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {browseLoading ? (
                    <div className="v-browse-loading">Lade...</div>
                  ) : (
                    <div className="v-browse-items">
                      <button className="v-browse-item pick" onClick={() => pickFolder(browsePath)}
                              title="Ziel bestätigen und schließen — der Pfad ist bereits übernommen">
                        <FaCheck /> Hier ablegen &amp; schließen
                      </button>
                      {newFolderMode ? (
                        <div className="v-new-folder-row">
                          <FaFolderPlus className="v-new-folder-icon" />
                          <input
                            className="v-new-folder-input"
                            autoFocus
                            placeholder="Ordner-Name..."
                            value={newFolderName}
                            onChange={(e) => setNewFolderName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") { createFolder(browsePath, newFolderName, browseFolder); setNewFolderMode(false); setNewFolderName(""); }
                              if (e.key === "Escape") { setNewFolderMode(false); setNewFolderName(""); }
                            }}
                          />
                          <button className="v-btn ghost small" title="Erstellen" onClick={() => { createFolder(browsePath, newFolderName, browseFolder); setNewFolderMode(false); setNewFolderName(""); }}><FaCheck /></button>
                          <button className="v-btn ghost small" title="Abbrechen" onClick={() => { setNewFolderMode(false); setNewFolderName(""); }}><FaTimes /></button>
                        </div>
                      ) : (
                        <button className="v-browse-item new-folder" onClick={() => { setNewFolderMode(true); setNewFolderName(""); }}>
                          <FaFolderPlus /> Neuen Ordner erstellen
                        </button>
                      )}
                      {browseItems.map((item) => (
                        item.is_folder !== false ? (
                          <button key={item.path} className="v-browse-item"
                            onClick={() => browseFolder(item.path)}>
                            <FaFolder /> {item.name}
                          </button>
                        ) : (
                          <div key={item.path} className="v-browse-item v-browse-file">
                            <FaFile /> {item.name}
                          </div>
                        )
                      ))}
                      {browseItems.length === 0 && <div className="v-browse-empty">Keine Unterordner</div>}
                    </div>
                  )}
                </div>
              )}
              {/* PDF Toolbar */}
              {pdfDoc && (
                <div className="v-pdf-toolbar">
                  <button className="v-pdf-tool-btn" title="Vorherige Seite" disabled={pdfPage <= 0}
                    onClick={() => setPdfPage(p => Math.max(0, p - 1))}>◀</button>
                  <span className="v-pdf-page-label">Seite {pdfPage + 1} / {pdfNumPages}</span>
                  <button className="v-pdf-tool-btn" title="Nächste Seite" disabled={pdfPage >= pdfNumPages - 1}
                    onClick={() => setPdfPage(p => Math.min(pdfNumPages - 1, p + 1))}>▶</button>
                  <span className="v-pdf-sep">|</span>
                  <button className="v-pdf-tool-btn" title="Verkleinern"
                    onClick={() => { setPdfZoom(z => Math.max(0.3, z / 1.25)); setPdfPan({ x: 0, y: 0 }); }}>−</button>
                  <span className="v-pdf-zoom-label">{Math.round(pdfZoom * 100)}%</span>
                  <button className="v-pdf-tool-btn" title="Vergrößern"
                    onClick={() => setPdfZoom(z => Math.min(5, z * 1.25))}>+</button>
                  <span className="v-pdf-sep">|</span>
                  <button className="v-pdf-tool-btn" title="90° links drehen" disabled={editMode}
                    onClick={() => setPdfRotation(r => (r - 90 + 360) % 360)}>↺</button>
                  <button className="v-pdf-tool-btn" title="90° rechts drehen" disabled={editMode}
                    onClick={() => setPdfRotation(r => (r + 90) % 360)}>↻</button>
                  <button className="v-pdf-tool-btn" title="Zurücksetzen"
                    onClick={() => { setPdfZoom(1); if (!editMode) setPdfRotation(0); setPdfPan({ x: 0, y: 0 }); }}>⟲</button>
                  {!editMode && (
                    <>
                      <span className="v-pdf-sep">|</span>
                      <button className="v-pdf-tool-btn v-edit-start" title="PDF bearbeiten (wie Vorschau)"
                        onClick={editorStart}><FaPen /></button>
                    </>
                  )}
                </div>
              )}
              {editMode && pdfDoc && (
                <div className="v-pdf-toolbar v-edit-toolbar">
                  {EDIT_TOOLS.map(({ key, Icon, title }) => (
                    <button key={key} title={title}
                      className={"v-pdf-tool-btn v-edit-tool" + (editTool === key ? " aktiv" : "")}
                      onClick={() => { setEditTool(key); setEditAktiveId(null); }}><Icon /></button>
                  ))}
                  <span className="v-pdf-sep">|</span>
                  <select className="v-edit-select" title="Schriftgröße" value={editGroesse}
                    onChange={(e) => setEditGroesse(Number(e.target.value))}>
                    {[8, 10, 12, 14, 18, 24].map((g) => <option key={g} value={g}>{g}</option>)}
                  </select>
                  {EDIT_FARBEN.map((f) => (
                    <button key={f} className={"v-edit-farbe" + (editFarbe === f ? " aktiv" : "")}
                      style={{ background: f }} title={`Farbe ${f}`} onClick={() => setEditFarbe(f)} />
                  ))}
                  <button className="v-pdf-tool-btn" title="Letzte Änderung zurücknehmen"
                    disabled={!editAnnos.length}
                    onClick={() => setEditAnnos((prev) => prev.slice(0, -1))}><FaUndo /></button>
                  <span className="v-pdf-sep">|</span>
                  <button className={"v-pdf-tool-btn v-edit-seite-weg" + (editLoeschen.includes(pdfPage) ? " aktiv" : "")}
                    title={editLoeschen.includes(pdfPage) ? "Seite doch behalten" : "Diese Seite löschen (wirkt beim Speichern)"}
                    disabled={!editLoeschen.includes(pdfPage) && editLoeschen.length >= pdfNumPages - 1}
                    onClick={() => setEditLoeschen((prev) => prev.includes(pdfPage)
                      ? prev.filter((x) => x !== pdfPage) : [...prev, pdfPage].sort((a, b) => a - b))}>
                    <FaTrash />{editLoeschen.length > 0 && <span className="v-edit-zahl">{editLoeschen.length}</span>}
                  </button>
                  <button className="v-pdf-tool-btn v-edit-vereinen" title="Mit anderem Dokument vereinen (Seiten hinten anhängen)"
                    onClick={() => {
                      if (editorHatAenderungen()) { alert("Bitte erst speichern oder abbrechen, dann vereinen."); return; }
                      setVereinenMit(null); setVereinenSuche(""); setVereinenOffen(true);
                    }}><FaObjectGroup /></button>
                  <span style={{ flex: 1 }} />
                  <button className="v-pdf-tool-btn v-edit-save" disabled={editSaving || !editorHatAenderungen()}
                    title="Speichern — Änderungen aller Seiten, Original wird ersetzt"
                    onClick={editorSpeichern}>{editSaving ? <FaSync className="spin" /> : <FaSave />}</button>
                  <button className="v-pdf-tool-btn v-edit-cancel" title="Abbrechen"
                    onClick={editorAbbrechen}><FaTimes /></button>
                </div>
              )}
              <div className="v-thumb-area"
                onMouseDown={(e) => { if (pdfZoom <= 1 || !pdfDoc) return; if (e.target.closest('.v-pdf-text-layer')) return; if (e.target.closest('.v-pdf-edit-layer')) return; pdfIsPanning.current = true; pdfPanStart.current = { x: e.clientX, y: e.clientY }; pdfPanOffset.current = { ...pdfPan }; }}
                onMouseMove={(e) => { if (!pdfIsPanning.current) return; setPdfPan({ x: pdfPanOffset.current.x + (e.clientX - pdfPanStart.current.x), y: pdfPanOffset.current.y + (e.clientY - pdfPanStart.current.y) }); }}
                onMouseUp={() => { pdfIsPanning.current = false; }}
                onMouseLeave={() => { pdfIsPanning.current = false; }}
              >
                {thumbLoading ? (
                  <div className="v-thumb-loading">
                    <div className="v-pulse-ring small" />
                    <span>PDF laden...</span>
                  </div>
                ) : pdfDoc ? (
                  <div
                    className="v-pdf-stage"
                    style={{
                      transform: `translate(${pdfPan.x}px, ${pdfPan.y}px)`,
                      cursor: pdfZoom > 1 ? (pdfIsPanning.current ? 'grabbing' : 'grab') : 'text',
                      transition: pdfIsPanning.current ? 'none' : 'transform 0.15s ease',
                    }}
                  >
                    <canvas ref={pdfCanvasRef} className="v-pdf-canvas" />
                    <div ref={pdfTextLayerRef} className="v-pdf-text-layer"
                         style={editMode ? { pointerEvents: "none" } : undefined} />
                    {editMode && pdfVp && (
                      <PdfEditLayer vp={pdfVp} seite={pdfPage} tool={editTool}
                        annos={editAnnos} setAnnos={setEditAnnos}
                        felder={editFelder} setFelder={setEditFelder}
                        groesse={editGroesse} farbe={editFarbe}
                        aktiveId={editAktiveId} setAktiveId={setEditAktiveId} />
                    )}
                    {editMode && editLoeschen.includes(pdfPage) && (
                      <div className="v-edit-seite-weg-hinweis">Seite {pdfPage + 1} wird beim Speichern gelöscht</div>
                    )}
                  </div>
                ) : (
                  <div className="v-thumb-empty">Keine Vorschau</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Duplikat-Popup Modal (Einzel) mit Side-by-Side PDF-Vergleich */}
      {duplikatInfo && (
        <div className="v-overlay" onClick={() => duplikatInfo.onAbort()}>
          <div className="v-modal v-duplikat-modal v-duplikat-compare" onClick={(e) => e.stopPropagation()}>
            <div className="v-modal-header">
              <h3>⚠️ Duplikat gefunden</h3>
              <button className="v-dismiss" onClick={() => duplikatInfo.onAbort()}>×</button>
            </div>
            <div className="v-duplikat-side-by-side">
              {/* LINKE SEITE: Neue Datei */}
              <div className="v-duplikat-side v-duplikat-source">
                <div className="v-duplikat-label" style={{ color: "#81c784" }}>
                  Neu (wird verschoben)
                  <button
                    className="v-duplikat-zoom"
                    title="In neuem Tab öffnen (zoombar)"
                    onClick={() => window.open(API_BASE + "/zuordnen/api/verschieben/pdf-download?path=" + encodeURIComponent(duplikatInfo.quell?.path || ""), "_blank")}
                  >
                    <FaExternalLinkAlt />
                  </button>
                </div>
                <div className="v-duplikat-name">{duplikatInfo.quell?.name}</div>
                <div className="v-duplikat-path" title={duplikatInfo.quell?.path}>{duplikatInfo.quell?.path}</div>
                <div className="v-duplikat-meta">
                  {fmtSize(duplikatInfo.quell?.size) != null && <span>{fmtSize(duplikatInfo.quell.size)}</span>}
                  {fmtDate(duplikatInfo.quell?.modified) != null && <span>{fmtDate(duplikatInfo.quell.modified)}</span>}
                </div>
                <iframe
                  className="v-duplikat-pdf"
                  title="Neue Datei"
                  src={API_BASE + "/zuordnen/api/verschieben/pdf-download?path=" + encodeURIComponent(duplikatInfo.quell?.path || "") + "#toolbar=1&zoom=page-width"}
                />
              </div>
              {/* RECHTE SEITE: Bestehende Datei */}
              <div className="v-duplikat-side v-duplikat-existing">
                <div className="v-duplikat-label" style={{ color: "#ef9a9a" }}>
                  Alt (bereits vorhanden)
                  <button
                    className="v-duplikat-zoom"
                    title="In neuem Tab öffnen (zoombar)"
                    onClick={() => window.open(API_BASE + "/zuordnen/api/verschieben/pdf-download?path=" + encodeURIComponent(duplikatInfo.existierend?.path || ""), "_blank")}
                  >
                    <FaExternalLinkAlt />
                  </button>
                </div>
                <div className="v-duplikat-name">{duplikatInfo.existierend?.name}</div>
                <div className="v-duplikat-path" title={duplikatInfo.existierend?.path}>{duplikatInfo.existierend?.path}</div>
                <div className="v-duplikat-meta">
                  {fmtSize(duplikatInfo.existierend?.size) != null
                    ? <span>{fmtSize(duplikatInfo.existierend.size)}</span>
                    : <span className="v-duplikat-meta-loading">Größe wird geladen…</span>}
                  {fmtDate(duplikatInfo.existierend?.modified) != null && <span>{fmtDate(duplikatInfo.existierend.modified)}</span>}
                </div>
                <iframe
                  className="v-duplikat-pdf"
                  title="Bestehende Datei"
                  src={API_BASE + "/zuordnen/api/verschieben/pdf-download?path=" + encodeURIComponent(duplikatInfo.existierend?.path || "") + "#toolbar=1&zoom=page-width"}
                />
              </div>
            </div>
            {/* Kopien beim Ersetzen (nur wenn Datev/Umb. ursprünglich angehakt) */}
            {(duplikatInfo.kopienWollte?.datev || duplikatInfo.kopienWollte?.umbenannt) && (
              <div className="v-duplikat-kopien">
                <span className="v-duplikat-kopien-title">Kopien beim Ersetzen:</span>
                {!duplikatInfo.kopienStatus && <span className="v-duplikat-kopien-info">prüfe Datev/Umbenannt…</span>}
                {duplikatInfo.kopienStatus?.fehler && (
                  <span className="v-duplikat-kopien-info">Prüfung fehlgeschlagen — es werden keine Kopien erstellt</span>
                )}
                {duplikatInfo.kopienStatus?.fertig && !duplikatInfo.kopienStatus.fehler && (
                  <>
                    {duplikatInfo.kopienWollte.datev && (
                      duplikatInfo.kopienStatus.datev_vorhanden ? (
                        <span className="v-duplikat-kopien-ok"><FaCheck /> Datev-Kopie existiert bereits — keine neue</span>
                      ) : (
                        <label className="v-duplikat-kopien-frage">
                          <input
                            type="checkbox"
                            checked={duplikatKopienRef.current.datev}
                            onChange={(e) => { duplikatKopienRef.current.datev = e.target.checked; setDuplikatInfo((p) => ({ ...p })); }}
                          />
                          Datev-Kopie erstellen
                        </label>
                      )
                    )}
                    {duplikatInfo.kopienWollte.umbenannt && (
                      duplikatInfo.kopienStatus.umbenannt_vorhanden ? (
                        <span className="v-duplikat-kopien-ok"><FaCheck /> Umbenannt-Kopie existiert bereits — keine neue</span>
                      ) : (
                        <label className="v-duplikat-kopien-frage">
                          <input
                            type="checkbox"
                            checked={duplikatKopienRef.current.umbenannt}
                            onChange={(e) => { duplikatKopienRef.current.umbenannt = e.target.checked; setDuplikatInfo((p) => ({ ...p })); }}
                          />
                          Umbenannt-Kopie erstellen
                        </label>
                      )
                    )}
                  </>
                )}
              </div>
            )}
            {/* Aktionen: eine ausgewogene Zeile */}
            <div className="v-modal-actions v-duplikat-actions">
              <button
                ref={duplikatReplaceRef}
                className="v-btn danger"
                onClick={() => duplikatInfo.onReplace()}
                title={"Neue Datei landet in: " + (duplikatInfo.existierend?.path?.replace(/\/[^/]+$/, "") || "")}
              >
                <FaTrash style={{ marginRight: 6 }} /> Alte löschen, Neue behalten
              </button>
              {duplikatInfo.onReplaceNew && (() => {
                const altOrd = duplikatInfo.existierend?.path?.replace(/\/[^/]+$/, "");
                const zielOrd = duplikatInfo.zielOrdner;
                const same = altOrd && zielOrd && altOrd.toLowerCase() === zielOrd.toLowerCase();
                return !same ? (
                  <button
                    className="v-btn"
                    style={{ background: "#5c3317" }}
                    onClick={() => duplikatInfo.onReplaceNew()}
                    title={"Neue Datei landet in: " + (zielOrd || "")}
                  >
                    <FaTrash style={{ marginRight: 6 }} /> Alte löschen → Zielordner
                  </button>
                ) : null;
              })()}
              <button className="v-btn danger" style={{ opacity: 0.8 }} onClick={() => duplikatInfo.onDelete()}>
                <FaTrash style={{ marginRight: 6 }} /> Neue löschen
              </button>
              {duplikatInfo.onKeepBoth && (
                <button className="v-btn ghost" onClick={() => duplikatInfo.onKeepBoth()}>
                  <FaLayerGroup style={{ marginRight: 6 }} /> Beide behalten
                </button>
              )}
              <button className="v-btn ghost" onClick={() => duplikatInfo.onAbort()}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {/* Duplikat-Liste Modal (Batch/Auto) */}
      {duplikatList.length > 0 && (
        <div className="v-overlay" onClick={() => setDuplikatList([])}>
          <div className="v-modal v-duplikat-modal" onClick={(e) => e.stopPropagation()}>
            <div className="v-modal-header">
              <h3>⚠️ {duplikatList.length} Duplikat{duplikatList.length > 1 ? "e" : ""} gefunden</h3>
              <button className="v-dismiss" onClick={() => setDuplikatList([])}>×</button>
            </div>
            <p className="v-modal-desc">
              Folgende Dateien existieren bereits identisch im Zielordner und wurden übersprungen.
            </p>
            <div className="v-batch-list">
              {duplikatList.map((d, i) => (
                <div key={i} className="v-batch-item v-duplikat-batch-item">
                  <span>{d.quell?.name || d.quell?.quell_pfad?.split("/").pop()}</span>
                  <span className="dim">= {d.existierend?.name}</span>
                </div>
              ))}
            </div>
            <div className="v-modal-actions">
              <button className="v-btn danger" onClick={async () => {
                for (const d of duplikatList) {
                  try {
                    const pfad = d.quell?.quell_pfad || d.quell?.path;
                    if (pfad) await apiPost("/verschieben/loeschen", { pfad });
                  } catch (e) { console.warn("Löschen fehlgeschlagen:", e); }
                }
                setDateien((prev) => {
                  const delPaths = new Set(duplikatList.map((d) => d.quell?.quell_pfad || d.quell?.path));
                  return prev.filter((f) => !delPaths.has(f.path));
                });
                setDuplikatList([]);
              }}>
                <FaTrash style={{ marginRight: 6 }} /> Alle {duplikatList.length} Quelldateien löschen
              </button>
              <button className="v-btn ghost" onClick={() => setDuplikatList([])}>
                Überspringen (nicht löschen)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NOK-Art Popup */}
      {nokPickerFile && (
        <div className="v-pipeline-overlay" onClick={() => setNokPickerFile(null)}>
          <div className="v-pipeline-modal" onClick={e => e.stopPropagation()}>
            <h3>NOK — Art wählen</h3>
            <p className="v-pipeline-subtitle">Was soll mit der Datei passieren?</p>
            <div className="v-pipeline-options">
              <button
                className="v-pipeline-btn v-nok-kopie"
                onClick={() => {
                  setFileSettings(prev => ({ ...prev, [nokPickerFile.path]: { ...prev[nokPickerFile.path], kopie_nok: true, nok_original: false } }));
                  setNokPickerFile(null);
                }}
              >
                <span className="v-pipeline-label">Kopie nach NOK</span>
                <span className="v-pipeline-desc">Original → Zielordner<br/>Kopie → NOK</span>
              </button>
              <button
                className="v-pipeline-btn v-nok-original"
                onClick={() => {
                  const f = nokPickerFile;
                  setNokPickerFile(null);
                  moveNokOriginal(f);
                }}
              >
                <span className="v-pipeline-label">Original → NOK</span>
                <span className="v-pipeline-desc">Direkt in NOK<br/>verschieben</span>
              </button>
            </div>
            <button className="v-pipeline-cancel" onClick={() => setNokPickerFile(null)}>Abbrechen</button>
          </div>
        </div>
      )}

      {/* Duplikat-Picker Popup */}
      {vereinenOffen && selected && (
        <div className="v-pipeline-overlay" onClick={() => !vereinenLaeuft && setVereinenOffen(false)}>
          <div className="v-pipeline-modal v-vereinen-modal" onClick={e => e.stopPropagation()}>
            <h3>Mit anderem Dokument vereinen</h3>
            <p className="v-pipeline-subtitle" style={{ wordBreak: "break-all" }}>
              Die Seiten des gewählten Dokuments kommen hinten an <b>{selected.name}</b>.
            </p>
            {!vereinenMit ? (
              <>
                <input className="v-vereinen-suche" autoFocus placeholder="Dokument suchen …" value={vereinenSuche}
                  onChange={(e) => setVereinenSuche(e.target.value)} />
                <div className="v-vereinen-liste">
                  {dateien.filter((d) => d.path !== selected.path && /\.pdf$/i.test(d.name || "")
                      && (d.name || "").toLowerCase().includes(vereinenSuche.trim().toLowerCase()))
                    .slice(0, 200).map((d) => (
                      <button key={d.path} className="v-vereinen-eintrag" onClick={() => setVereinenMit(d)}>{d.name}</button>
                    ))}
                </div>
              </>
            ) : (
              <div className="v-vereinen-frage">
                <p><b>{vereinenMit.name}</b> hinten an dieses Dokument anhängen?</p>
                <p style={{ opacity: 0.8, fontSize: 13 }}>Das zweite Dokument wandert danach in den Dropbox-Papierkorb
                  (von dort wiederherstellbar).</p>
                <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                  <button className="v-btn" disabled={vereinenLaeuft} onClick={vereinenAusfuehren}>
                    {vereinenLaeuft ? "Vereint …" : "Vereinen"}</button>
                  <button className="v-btn ghost" disabled={vereinenLaeuft} onClick={() => setVereinenMit(null)}>Anderes wählen</button>
                </div>
              </div>
            )}
            <button className="v-pipeline-cancel" disabled={vereinenLaeuft} onClick={() => setVereinenOffen(false)}>Abbrechen</button>
          </div>
        </div>
      )}

      {duplikatPickerFile && (
        <div className="v-pipeline-overlay" onClick={() => setDuplikatPickerFile(null)}>
          <div className="v-pipeline-modal" onClick={e => e.stopPropagation()}>
            <h3>Duplikat erstellen</h3>
            <p className="v-pipeline-subtitle" style={{ wordBreak: "break-all" }}>{duplikatPickerFile.name}</p>
            <div className="v-pipeline-options">
              <button
                className="v-pipeline-btn"
                onClick={() => { duplizierenJetzt(duplikatPickerFile.path); }}
              >
                <span className="v-pipeline-label">Jetzt duplizieren</span>
                <span className="v-pipeline-desc">Kopie „(Kopie)" im selben<br/>Ordner — ohne Verschieben</span>
              </button>
              <button
                className="v-pipeline-btn"
                onClick={() => {
                  const f = duplikatPickerFile;
                  setFileSettings(prev => ({ ...prev, [f.path]: { ...prev[f.path], duplikat_quelle: true } }));
                  setDuplikatPickerFile(null);
                }}
              >
                <span className="v-pipeline-label">Duplikat nach Verschieben</span>
                <span className="v-pipeline-desc">Kopie bleibt im Quellordner,<br/>Original wandert ins Ziel</span>
              </button>
            </div>
            {(fileSettings[duplikatPickerFile.path] || {}).duplikat_quelle && (
              <button className="v-pipeline-cancel" style={{ color: "#da70d6" }}
                onClick={() => {
                  const f = duplikatPickerFile;
                  setFileSettings(prev => ({ ...prev, [f.path]: { ...prev[f.path], duplikat_quelle: false } }));
                  setDuplikatPickerFile(null);
                }}
              >„Nach Verschieben"-Duplikat wieder abschalten</button>
            )}
            <button className="v-pipeline-cancel" onClick={() => setDuplikatPickerFile(null)}>Abbrechen</button>
          </div>
        </div>
      )}

      {/* Pipeline-Picker Popup — Welle 80: gemeinsame Komponente EnginePicker */}
      {pipelinePickerFile && (
        <EnginePicker
          file={{ name: pipelinePickerFile.name }}
          title="OCR-Pipeline wählen"
          subtitle="Welche Engine soll für die Analyse verwendet werden?"
          options={[
            { key: "v1", label: "Pipeline 1", desc: "MuPDF + Tesseract", accent: "v1" },
            { key: "gemini", label: "Pipeline 2", desc: "✨ Gemini (Dokument direkt)", accent: "v2" },
            { key: "v3", label: "Pipeline 3", desc: "PaddleOCR", accent: "v3" },
          ]}
          extra={(
            <div className="v-pipeline-schema-row">
              <label className="v-pipeline-schema-label">Schema erzwingen (Pipeline 1):</label>
              <select
                className="v-pipeline-schema-select"
                value={pipelineSchemaOverride}
                onClick={e => e.stopPropagation()}
                onChange={e => setPipelineSchemaOverride(e.target.value)}
              >
                <option value="">— automatisch —</option>
                {SCHEMA_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          )}
          onChoose={(key) => {
            const f = pipelinePickerFile; const so = pipelineSchemaOverride;
            if (key === "v1") reprocessFile(f, "v1", so);
            else if (key === "v3") reprocessFile(f, "v3");
            else if (key === "gemini") { setKi2Chat([]); startKi2(f, "gemini"); }
            setPipelineSchemaOverride("");
          }}
          onClose={() => { setPipelinePickerFile(null); setPipelineSchemaOverride(""); }}
        />
      )}

      {/* Pipeline 2 — Ergebnis-Modal */}
      {(ki2Polling || ki2Result) && (
        <div className="v-pipeline-overlay" onClick={() => { clearInterval(ki2PollRef.current); setKi2Polling(false); setKi2Result(null); setKi2File(null); }}>
          <div className="v-pipeline-modal" onClick={e => e.stopPropagation()}>
            <h3>⚡ Pipeline 2 — {ki2Model === "gemini" ? "✨ Gemini" : ki2Model === "gemma" ? "🦙 Gemma 4" : "🤖 Claude Haiku"}</h3>
            {ki2File && <p className="v-pipeline-subtitle">📄 {ki2File.name}</p>}

            {ki2Polling && !ki2Result && (
              <div style={{ textAlign: "center", padding: "1.5rem 0" }}>
                <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>{ki2Model === "gemini" ? "✨" : ki2Model === "gemma" ? "🦙" : "🤖"}</div>
                <p style={{ color: "#aaa", fontSize: "0.9rem" }}>
                  {ki2Model === "gemini" ? "Gemini liest das Dokument…" : ki2Model === "gemma" ? "Gemma 4 analysiert…" : "Analyse läuft im Terminal…"}
                </p>
                <p style={{ color: "#666", fontSize: "0.8rem" }}>Ergebnis wird automatisch angezeigt</p>
              </div>
            )}

            {ki2Result && ki2Result.status === "done" && ki2Result.result && (() => {
              const r = ki2Result.result;
              return (
                <div style={{ marginTop: "1rem" }}>
                  {/* Welle 75: Verlauf — frühere Runden bleiben sichtbar, Korrekturen bauen aufeinander auf */}
                  {ki2Chat.length > 0 && (
                    <div style={{ marginBottom: "0.75rem", fontSize: "0.78rem", color: "#999", borderLeft: "3px solid #444", paddingLeft: "0.6rem" }}>
                      <div style={{ color: "#aaa", marginBottom: "0.25rem" }}>🧠 Verlauf ({ki2Chat.length} Korrektur{ki2Chat.length > 1 ? "en" : ""}, Gemini merkt sich alles):</div>
                      {ki2Chat.map((c, i) => (
                        <div key={i} style={{ marginBottom: "0.35rem" }}>
                          <div style={{ color: "#777" }}>Runde {i + 1}: <span style={{ color: "#bbb" }}>{c.result?.proposed_filename || "—"}</span></div>
                          <div style={{ color: "#aaaaff" }}>↳ Korrektur: {c.korrektur}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ background: "#1a2a1a", border: "1px solid #2a4a2a", borderRadius: "6px", padding: "1rem", fontFamily: "monospace", fontSize: "0.85rem" }}>
                    <div style={{ color: "#7fff7f", fontWeight: "bold", marginBottom: "0.5rem" }}>✅ Vorgeschlagener Dateiname:</div>
                    <div style={{ color: "#fff", fontSize: "1rem", marginBottom: "1rem" }}>{r.proposed_filename}</div>
                    <table style={{ width: "100%", color: "#ccc", borderCollapse: "collapse" }}>
                      <tbody>
                        {r.prefix && <tr><td style={{ color: "#888", paddingRight: "1rem" }}>Prefix</td><td>{r.prefix}</td></tr>}
                        {r.ki_objekt && <tr><td style={{ color: "#888", paddingRight: "1rem" }}>Objekt (KI)</td><td style={{ color: "#aaffaa" }}>{r.ki_objekt}</td></tr>}
                        {(r.sender_kuerzel || r.ki_absender) && <tr><td style={{ color: "#888", paddingRight: "1rem" }}>Absender</td><td>{r.sender_kuerzel || r.ki_absender}</td></tr>}
                        {r.schema && <tr><td style={{ color: "#888", paddingRight: "1rem" }}>Schema</td><td>{r.schema}</td></tr>}
                        {(() => {
                          const d = r.ki_raw?.datum;
                          if (!d) return null;
                          const val = Array.isArray(d) ? d.map(x => `${x.wert} (${x.gewichtung}★)`).join(", ") : d;
                          return <tr><td style={{ color: "#888", paddingRight: "1rem" }}>Datum</td><td>{val}</td></tr>;
                        })()}
                        {(() => {
                          const nr = r.ki_raw?.nummern?.rechnungsnummer || r.ki_raw?.rechnungsnummer;
                          return nr ? <tr><td style={{ color: "#888", paddingRight: "1rem" }}>Rechnungs-Nr</td><td>{nr}</td></tr> : null;
                        })()}
                        {r.confidence && <tr><td style={{ color: "#888", paddingRight: "1rem" }}>Konfidenz</td><td>{Math.round(r.confidence * 100)}%</td></tr>}
                      </tbody>
                    </table>
                    {/* Alle erkannten Empfänger/Objekte anzeigen wenn mehrere */}
                    {Array.isArray(r.ki_raw?.empfaenger) && r.ki_raw.empfaenger.length > 1 && (
                      <div style={{ marginTop: "0.5rem", fontSize: "0.75rem", color: "#777" }}>
                        Empfänger-Kandidaten: {r.ki_raw.empfaenger.map(e => `${e.name} (${e.gewichtung}★)`).join(" · ")}
                      </div>
                    )}
                    {Array.isArray(r.ki_raw?.objekte) && r.ki_raw.objekte.length > 0 && (
                      <div style={{ marginTop: "0.25rem", fontSize: "0.75rem", color: "#777" }}>
                        Objekte: {r.ki_raw.objekte.map(o => `${o.bezeichnung} (${o.gewichtung}★)`).join(" · ")}
                      </div>
                    )}
                  </div>

                  {/* Feedback-Bereich */}
                  <div style={{ marginTop: "1rem", background: "#1a1a2a", border: "1px solid #333", borderRadius: "6px", padding: "0.75rem" }}>
                    <div style={{ color: "#aaa", fontSize: "0.8rem", marginBottom: "0.5rem" }}>💬 Etwas falsch? Korrektur eingeben:</div>
                    <textarea
                      value={ki2Feedback}
                      onChange={e => setKi2Feedback(e.target.value)}
                      placeholder="z.B. 'Der Prefix sollte 023 sein, nicht HIG' oder 'Das Datum ist falsch'"
                      style={{ width: "100%", background: "#111", color: "#ddd", border: "1px solid #444", borderRadius: "4px", padding: "0.5rem", fontSize: "0.82rem", fontFamily: "inherit", resize: "vertical", minHeight: "60px", boxSizing: "border-box" }}
                      disabled={ki2FeedbackLoading}
                    />
                    {/* Welle 127: Korrekturen werden keine Regeln mehr — beim Verschieben merkt sich Gemini den richtigen Namen als Beispiel */}
                    <div style={{ marginTop: "0.4rem", fontSize: "0.78rem", color: "#aaa" }}>
                      🧠 Gemini lernt automatisch: Wird die Datei verschoben, merkt es sich den endgültigen Namen mit deiner Korrektur als Beispiel.
                    </div>
                    <button
                      style={{ marginTop: "0.5rem", background: ki2FeedbackLoading ? "#222" : "#2a2a4a", border: "1px solid #5555aa", borderRadius: "4px", padding: "0.4rem 1rem", color: "#aaaaff", cursor: ki2FeedbackLoading ? "not-allowed" : "pointer", fontSize: "0.82rem" }}
                      disabled={ki2FeedbackLoading || !ki2Feedback.trim()}
                      onClick={async () => {
                        if (!ki2SessionId || !ki2Feedback.trim()) return;
                        setKi2FeedbackLoading(true);
                        try {
                          const korrektur = ki2Feedback;
                          await apiPost(`/verschieben/ki-claude/feedback/${ki2SessionId}`, { correction: korrektur });
                          // alte Runde in den Verlauf — Gemini kennt sie (History), der Nutzer sieht sie
                          setKi2Chat(prev => [...prev, { korrektur, result: r }]);
                          setKi2Feedback("");
                          setKi2Result(null);
                          setKi2Polling(true);
                          // Polling neu starten (war nach "done" gestoppt)
                          clearInterval(ki2PollRef.current);
                          ki2PollRef.current = setInterval(async () => {
                            try {
                              const pr = await fetch(API_BASE + "/zuordnen/api/verschieben/ki-claude/result/" + ki2SessionId);
                              if (!pr.ok) return;
                              const pd = await pr.json();
                              if (pd.status === "done" || pd.status === "error") {
                                clearInterval(ki2PollRef.current);
                                setKi2Polling(false);
                                setKi2Result(pd);
                              }
                            } catch (e) { console.warn("Ki2 Poll Fehler:", e); }
                          }, 3000);
                        } catch (e) {
                          alert("Feedback fehlgeschlagen: " + e.message);
                        } finally {
                          setKi2FeedbackLoading(false);
                        }
                      }}
                    >{ki2FeedbackLoading ? "⏳ Wird verarbeitet…" : "✉️ Korrektur senden"}</button>
                  </div>

                  <div style={{ display: "flex", gap: "0.75rem", marginTop: "1rem" }}>
                    <button
                      className="v-pipeline-btn"
                      style={{ flex: 1, background: "#1a4a1a", border: "1px solid #4a8a4a", borderRadius: "6px", padding: "0.6rem", cursor: "pointer", color: "#7fff7f", fontWeight: "bold" }}
                      onClick={async () => {
                        if (!ki2File || !r.proposed_filename) return;
                        const oldPath = ki2File.path;
                        let newName = r.proposed_filename;
                        const parentDir = oldPath.replace(/\/[^/]+$/, "");
                        let newPath = `${parentDir}/${newName}`;
                        try {
                          if (newName !== ki2File.name) {
                            const ren = await apiPost("/verschieben/umbenennen", { alter_pfad: oldPath, neuer_name: newName,
                              quelle: "gemini", session_id: ki2SessionId, proposed: r.proposed_filename });
                            // Name schon vergeben → Backend hängt „ (1)“ an
                            if (ren?.neuer_pfad) { newPath = ren.neuer_pfad; newName = ren.neuer_name; }
                          } else {
                            // Name identisch → trotzdem als Gemini-Übernahme protokollieren
                            try { await apiPost("/verschieben/umbenennen", { alter_pfad: oldPath, neuer_name: newName,
                              quelle: "gemini", session_id: ki2SessionId, proposed: r.proposed_filename }); } catch {}
                          }
                          // dateien[] + Status wie bei reprocessFile aktualisieren, damit die
                          // Datei NICHT ohne Status/Ziel liegen bleibt. Welle 78: keine harte
                          // Ampel, keine alte Gruppe — analyseEinzel entscheidet.
                          setDateien(prev => prev.map(d => d.path === oldPath ? { ...d, path: newPath, name: newName } : d));
                          setFileSettings(prev => {
                            const { [oldPath]: oldSett, ...rest } = prev;
                            return { ...rest, [newPath]: { ...(oldSett || {}), confidence: "none",
                              reason: `Gemini: ${r.schema || "?"} → ${newName}` + (r.sender_kuerzel ? ` (${r.sender_kuerzel})` : "") } };
                          });
                          moveLock(oldPath, newPath);   // Welle 85: Sektion bleibt
                          try { delete pdfDocCache.current[oldPath]; } catch {}
                          setKi2Polling(false); setKi2Result(null); setKi2File(null);
                          // Zielordner-Vorschlag auf Basis des neuen Namens holen (nur Farbe/Ordner)
                          await analyseEinzel({ path: newPath, name: newName });
                          lockSection(newPath, fileSettingsRef.current?.[newPath]?.confidence);
                        } catch (e) { alert("Umbenennen fehlgeschlagen: " + e.message); }
                      }}
                    >✅ Übernehmen</button>
                    <button
                      className="v-pipeline-btn"
                      style={{ flex: 1, background: "#1a1a2a", border: "1px solid #4a4a8a", borderRadius: "6px", padding: "0.6rem", cursor: "pointer", color: "#aaaaff" }}
                      title="Neue Session ohne Verlauf starten"
                      onClick={() => { setKi2Chat([]); setKi2Result(null); setKi2Polling(false); if (ki2File) startKi2(ki2File, ki2Model); }}
                    >🔄 Nochmal</button>
                    <button
                      className="v-pipeline-btn v-pipeline-cancel"
                      style={{ flex: 1 }}
                      onClick={() => { clearInterval(ki2PollRef.current); setKi2Polling(false); setKi2Result(null); setKi2File(null); }}
                    >Schließen</button>
                  </div>
                </div>
              );
            })()}

            {ki2Result && ki2Result.status === "error" && (
              <div style={{ marginTop: "1rem", color: "#ff8080" }}>
                <p>❌ Analyse fehlgeschlagen</p>
                <p style={{ fontSize: "0.8rem", color: "#888" }}>{ki2Result.result?.error || "Unbekannter Fehler"}</p>
                <button className="v-pipeline-cancel" onClick={() => { setKi2Result(null); setKi2File(null); }}>Schließen</button>
              </div>
            )}

            {!ki2Result && (
              <button className="v-pipeline-cancel" style={{ marginTop: "1rem" }} onClick={() => { clearInterval(ki2PollRef.current); setKi2Polling(false); setKi2File(null); }}>Abbrechen</button>
            )}
          </div>
        </div>
      )}

      {/* Trello Popup */}
      {trelloPopupOpen && trelloPopupPath && (() => {
        const tDatei = dateien.find(d => d.path === trelloPopupPath);
        const tSett = fileSettings[trelloPopupPath] || {};
        const tFilename = tDatei?.name || trelloPopupPath.split("/").pop();
        const tZiel = tSett.ziel_ordner || "";
        const tVorschau = [
          `**Datei:** ${tFilename}`,
          `**Quell-Pfad:** ${trelloPopupPath}`,
          tZiel ? `**Ziel-Ordner:** ${tZiel}` : null,
          trelloGrund ? `\n**Grund / Anmerkung:**\n${trelloGrund}` : null,
          `\n*Erstellt via IMHO Verschieben*`,
        ].filter(Boolean).join("\n");
        return (
          <div className="v-overlay" onClick={() => { if (!trelloSaving) setTrelloPopupOpen(false); }}>
            <div className="v-trello-modal v-modal" onClick={e => e.stopPropagation()}>
              <div className="v-modal-header">
                <h3 style={{ color: "#9c27b0" }}>🟣 Trello-Card erstellen</h3>
                <button className="v-dismiss" onClick={() => setTrelloPopupOpen(false)}>&#215;</button>
              </div>
              <div className="v-trello-body">
                <label className="v-strg-field">
                  <span>Card-Titel</span>
                  <input
                    value={trelloTitel}
                    onChange={e => setTrelloTitel(e.target.value)}
                    placeholder={tFilename}
                  />
                </label>
                <label className="v-strg-field">
                  <span>Dropbox-Pfad</span>
                  <input readOnly value={trelloPopupPath} style={{ opacity: 0.6, fontSize: "0.75rem" }} />
                </label>
                {tZiel && (
                  <label className="v-strg-field">
                    <span>Ziel-Ordner</span>
                    <input readOnly value={tZiel} style={{ opacity: 0.6, fontSize: "0.75rem" }} />
                  </label>
                )}
                <label className="v-strg-field">
                  <span>Warum / Anmerkung</span>
                  <textarea
                    rows={3}
                    placeholder="z.B. Absender nicht erkannt, Datum prüfen, manuell zuordnen, ..."
                    value={trelloGrund}
                    onChange={e => {
                      const newGrund = e.target.value;
                      setTrelloGrund(newGrund);
                      // Titel auto-aktualisieren wenn er noch dem bereinigten Dateinamen entspricht
                      const base = cleanFilenameForTrello(tFilename);
                      if (trelloTitel === base || trelloTitel === buildTrelloTitle(tFilename, trelloGrund)) {
                        setTrelloTitel(buildTrelloTitle(tFilename, newGrund));
                      }
                    }}
                    style={{ resize: "vertical", minHeight: 64 }}
                  />
                </label>
                <div className="v-trello-preview">
                  <div className="v-trello-preview-label">Vorschau Beschreibung:</div>
                  <pre className="v-trello-preview-text">{tVorschau}</pre>
                </div>
                {trelloError && <div className="v-strg-error">{trelloError}</div>}
                {trelloSuccess && <div className="v-strg-success">{trelloSuccess}</div>}
              </div>
              <div className="v-modal-actions">
                <button
                  ref={trelloBeimVerschiebenRef}
                  className="v-btn primary"
                  style={{ background: "#9c27b0", borderColor: "#9c27b0" }}
                  disabled={trelloSaving}
                  title="Card wird erst beim Verschieben erstellt"
                  onClick={() => {
                    setFileSettings(prev => ({
                      ...prev,
                      [trelloPopupPath]: { ...prev[trelloPopupPath], trello_enabled: true, trello_mode: "beim_verschieben", trello_grund: trelloGrund, trello_titel: trelloTitel },
                    }));
                    setTrelloPopupOpen(false);
                  }}
                >
                  📋 Card beim Verschieben
                </button>
                <button
                  className="v-btn"
                  style={{ borderColor: "#9c27b0", color: "#9c27b0" }}
                  disabled={trelloSaving}
                  title="Card jetzt sofort auf Trello erstellen + PDF anhängen"
                  onClick={async () => {
                    const res = await trelloCreateCard(trelloPopupPath, "direkt", trelloGrund, trelloTitel);
                    if (res) setTimeout(() => setTrelloPopupOpen(false), 1200);
                  }}
                >
                  {trelloSaving ? "Erstelle..." : "⚡ Direkt Card erstellen"}
                </button>
                <button className="v-btn ghost" disabled={trelloSaving} onClick={() => setTrelloPopupOpen(false)}>Abbrechen</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Batch Modal */}
      {showBatch && (
        <div className="v-overlay" onClick={() => setShowBatch(false)}>
          <div className="v-modal" onClick={(e) => e.stopPropagation()}>
            <div className="v-modal-header">
              <h3><FaLayerGroup /> {batchIsChecked ? "Markierte verschieben" : "Batch Verschieben"}</h3>
              <button className="v-dismiss" onClick={() => setShowBatch(false)}>&#215;</button>
            </div>
            <p className="v-modal-desc">{batchDisplayList.length} Dateien verschieben:</p>
            <div className="v-batch-stats">
              <span className="v-tag green">{batchDisplayList.filter((d) => fileSettings[d.path]?.confidence === "green").length} sicher</span>
              {batchDisplayList.filter((d) => fileSettings[d.path]?.confidence === "manuell").length > 0 && <span className="v-tag manuell">{batchDisplayList.filter((d) => fileSettings[d.path]?.confidence === "manuell").length} manuell</span>}
              {batchDisplayList.filter((d) => fileSettings[d.path]?.confidence === "wiederholen").length > 0 && <span className="v-tag wiederholen">{batchDisplayList.filter((d) => fileSettings[d.path]?.confidence === "wiederholen").length} wiederholen</span>}
            </div>
            <div className="v-batch-list">
              {batchDisplayList.map((d) => {
                const s = fileSettings[d.path] || {};
                const ziel = s.confidence === "manuell"     ? manuellOrdner
                           : s.confidence === "wiederholen" ? wiederholenOrdner
                           : s.ziel_ordner;
                return (
                  <div key={d.path} className="v-batch-item">
                    <span>{d.name}</span>
                    <span className="dim">{"→ "}{shortPath(ziel)}</span>
                    {s.confidence === "manuell" && <span className="v-tag manuell" style={{ fontSize: "0.7rem", padding: "1px 5px" }}>★</span>}
                  </div>
                );
              })}
            </div>
            <div className="v-modal-actions">
              <button className="v-btn primary" onClick={batchIsChecked ? handleCheckedBatch : handleBatch} disabled={batchMoving}>
                {batchMoving ? "Verschiebe..." : (batchIsChecked ? "Markierte " : "Alle ") + batchDisplayList.length + " verschieben"}
              </button>
              <button className="v-btn ghost" onClick={() => setShowBatch(false)} disabled={batchMoving}>
                Abbrechen
              </button>
            </div>
            {batchResult && (
              <div className={"v-toast " + (batchResult.fehler ? "error" : "success")}>
                {batchResult.fehler
                  ? batchResult.fehler
                  : batchResult.erfolgreich + " ok, " + batchResult.fehlgeschlagen + " fehl"}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hintergrund-Verschiebe-Jobs: Fortschritt + Ergebnis pro Datei */}
      <VerschiebenJobIndicator jobs={jobs} onDismiss={dismissJob} vorlauf={jobVorlauf} />
    </div>
  );
}

export default VerschiebenPage;
